import { Star, Clock, ShoppingCart, Search, X, Plus, Minus, Tag, AlertCircle, Flame, Filter, UtensilsCrossed, Store, LogOut, MapPin, Home, Briefcase, Navigation, Receipt, Settings, ArrowRight, Sparkles } from 'lucide-react';
import { User } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Restaurant,
  MenuItem,
  MenuItemVariant,
  MenuItemAddOn,
  Coupon,
  Category,
  UserType,
} from '../../App';
import { calculateDiscount } from '../../lib/mockData';
import { couponDataService } from '../../lib/dataService';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { CustomizeItemModal } from './CustomizeItemModal';
import { RestaurantDiscovery } from './RestaurantDiscovery';
import { MenuFilterModal } from './MenuFilterModal';
import { RestaurantTabs } from './RestaurantTabs';
import { DineOutView } from './DineOutView';
import { PaymentMethodModal } from './PaymentMethodModal';
import { OrderSuccessModal } from './OrderSuccessModal';
import { OrderTrackingModal } from './OrderTrackingModal';
import { OrderHistoryModal } from './OrderHistoryModal';
import { OrderReviewModal } from './OrderReviewModal';
import { BottomOrderTrackingBar } from './BottomOrderTrackingBar';
import { LocationSelectionModal } from './LocationSelectionModal';
import { CustomerSettings } from './CustomerSettings';
import { ProfileDropdown } from './ProfileDropdown';
import { ToastContainer, useToast } from '../ui/Toast';
import api from '../../lib/api';
import { AuthRequiredModal } from './AuthRequiredModal';
import { useLocale } from '../../lib/locale';

interface CustomerViewProps {
  user: UserType;
  onLogout: () => void;
}

interface CartItem {
  item: MenuItem;
  quantity: number;
  selectedVariant: MenuItemVariant | null;
  selectedAddOns: MenuItemAddOn[];
  specialInstructions: string;
  cartKey: string;
}

type DietaryFilter = string;
type SortOption = 'relevance' | 'price-low' | 'price-high' | 'rating';
type OrderMode = 'dine-out' | 'order-online' | null;

interface SavedAddress {
  id: string;
  type: 'Home' | 'Work' | 'Other';
  address: string;
  landmark?: string;
  isPrimary?: boolean;
}

const parseJsonArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeDietaryTag = (value: unknown): string => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  const cleaned = raw.replace(/[_\s]+/g, '-').replace(/-+/g, '-');
  if (cleaned === 'nonveg' || cleaned === 'non-veg' || cleaned === 'nonvegetarian' || cleaned === 'non-vegetarian') return 'non-veg';
  if (cleaned === 'veg' || cleaned === 'vegetarian') return 'veg';
  return cleaned;
};

const formatServingSizeLabel = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const lower = raw.toLowerCase();
  if (lower.includes('serve')) return raw;

  const rangeMatch = raw.match(/^(\d+)\s*-\s*(\d+)$/);
  if (rangeMatch) return `Serves ${rangeMatch[1]}-${rangeMatch[2]}`;

  if (/^\d+$/.test(raw)) return `Serves ${raw}`;
  return raw;
};

export function CustomerView({ user, onLogout }: CustomerViewProps) {
  const CART_STORAGE_KEY = `customer_cart_v1_${user?.id || 'guest'}`;
  const CARD_STORAGE_KEY = `customer_cards_${user?.id || user?.email || 'guest'}`;
  const WALLET_STORAGE_KEY = `customer_payment_links_${user?.id || user?.email || 'guest'}`;
  const { t, formatMoney } = useLocale();
  const { toasts, removeToast, warning } = useToast();
  const [showAuthRequired, setShowAuthRequired] = useState(false);
  const [orderMode, setOrderMode] = useState<OrderMode>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [isHeaderDescriptionExpanded, setIsHeaderDescriptionExpanded] = useState(false);
  const headerDescriptionRef = useRef<HTMLSpanElement | null>(null);
  const [isHeaderDescriptionOverflowing, setIsHeaderDescriptionOverflowing] = useState(false);
  const [menuRedirectUrlActive, setMenuRedirectUrlActive] = useState(false);
  const [publicKeyBootLoading, setPublicKeyBootLoading] = useState(false);
  const [resolvedPublicMenuKey, setResolvedPublicMenuKey] = useState('');
  const [restaurantRatingOverrides, setRestaurantRatingOverrides] = useState<Record<string, { rating: number; totalRatings: number }>>({});
  const [cart, setCart] = useState<Map<string, CartItem>>(new Map());
  const [activeCartRestaurantId, setActiveCartRestaurantId] = useState<string | null>(null);
  const [openCartOnRestaurantLoad, setOpenCartOnRestaurantLoad] = useState(false);
  const lastRestaurantRef = useRef<Restaurant | null>(null);
  const [showCart, setShowCart] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponError, setCouponError] = useState('');
  const [restaurantCoupons, setRestaurantCoupons] = useState<Coupon[]>([]);
  const [restaurantOffers, setRestaurantOffers] = useState<Coupon[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<Coupon | null>(null);
  const [customizingItem, setCustomizingItem] = useState<MenuItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('relevance');
  const [dietaryFilter, setDietaryFilter] = useState<DietaryFilter>(() => {
    const saved = localStorage.getItem('dietaryMode');
    return saved ? String(saved) : 'all';
  });
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [spicyLevelFilter, setSpicyLevelFilter] = useState<number[]>([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [showOrderTracking, setShowOrderTracking] = useState(false);
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(null);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [reviewingOrder, setReviewingOrder] = useState<{
    id: string;
    restaurantId: string;
    restaurantName?: string;
    rating?: number;
  } | null>(null);
  const [showBottomTracker, setShowBottomTracker] = useState(false);
  const [showSettings, setShowSettings] = useState<string | null>(null);
  const [modeAvatarError, setModeAvatarError] = useState(false);
  const [deliveryModeSelection, setDeliveryModeSelection] = useState<'RESTAURANT_SELF' | 'PLATFORM_PARTNER'>(
    'PLATFORM_PARTNER'
  );
  const keyFromUrl = (() => {
    const path = String(window.location.pathname || '').trim();
    const slugMatch = path.match(/^\/[a-z0-9._-]+\/([a-f0-9]{16})\/?$/i);
    if (slugMatch?.[1]) return String(slugMatch[1]).toLowerCase();
    const match = path.match(/^\/([a-f0-9]{16})\/?$/i);
    return match?.[1] ? String(match[1]).toLowerCase() : '';
  })();

  const navigateToPath = (path: string, replace = false) => {
    if (window.location.pathname === path) return;
    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const rememberPostLoginRedirect = (fallback = '/customer') => {
    try {
      const current = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`.trim();
      if (current && !current.startsWith('/customer-sign-')) {
        sessionStorage.setItem('post_login_redirect', current);
      } else if (fallback) {
        sessionStorage.setItem('post_login_redirect', fallback);
      }
    } catch {
      // ignore
    }
  };

  const navigateToCustomerAuth = (path: '/customer-sign-in' | '/customer-sign-up') => {
    rememberPostLoginRedirect();
    navigateToPath(path);
  };

  const isAuthenticated = Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
  const isGuestUser = !isAuthenticated || String(user?.id || '') === 'guest';
  const authRequiredMessage = 'To place your order now, log in to your existing account or sign up.';

  useEffect(() => {
    if (orderMode !== null) return;
    if (window.location.pathname === '/customer/dine-out') {
      setOrderMode('dine-out');
      return;
    }
    if (window.location.pathname === '/customer') {
      setOrderMode('order-online');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem('customer_initial_search');
    if (!raw) return;
    const normalized = String(raw || '').trim();
    if (normalized) setSearchQuery(normalized);
    sessionStorage.removeItem('customer_initial_search');
  }, []);

  useEffect(() => {
    if (selectedRestaurant) return;
    const raw = sessionStorage.getItem('post_login_selected_restaurant');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.id) {
        setSelectedRestaurant(parsed as Restaurant);
        setOrderMode('order-online');
      }
    } catch {
      // ignore parse errors
    } finally {
      sessionStorage.removeItem('post_login_selected_restaurant');
    }
  }, [selectedRestaurant]);

  const clearPublicMenuKeyFromUrl = () => {
    const path = String(window.location.pathname || '').trim();
    if (
      /^\/[a-f0-9]{16}\/?$/i.test(path) ||
      /^\/r\/[a-f0-9]{16}\/?$/i.test(path) ||
      /^\/[a-z0-9._-]+\/[a-f0-9]{16}\/?$/i.test(path)
    ) {
      window.history.replaceState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const getEffectiveItemPrice = (menuItem: MenuItem): number => {
    const raw = (menuItem as any)?.discountPrice ?? (menuItem as any)?.discount_price ?? menuItem.price;
    const resolved = Number(raw);
    return Number.isFinite(resolved) ? resolved : Number(menuItem.price || 0);
  };

  const parseCouponDateMs = (value: unknown): number | null => {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isCouponActiveToday = (coupon: Coupon): boolean => {
    if (!coupon.isActive) return false;
    const now = new Date();
    const todayYmd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const toYmd = (ms: number) => {
      const d = new Date(ms);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const fromMs = parseCouponDateMs(coupon.validFrom);
    const untilMs = parseCouponDateMs(coupon.validUntil);
    if (fromMs !== null && todayYmd < toYmd(fromMs)) return false;
    if (untilMs !== null && todayYmd > toYmd(untilMs)) return false;
    return true;
  };

  useEffect(() => {
    let mounted = true;

    if (!selectedRestaurant?.id) {
      setRestaurantCoupons([]);
      return () => {
        mounted = false;
      };
    }

    const toNumberSafe = (value: unknown, fallback = 0): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const normalizeCoupon = (row: any): Coupon => {
      const typeRaw = String(row?.discount_type_code ?? row?.discount_type ?? row?.discountTypeCode ?? row?.discountType ?? '')
        .trim()
        .toLowerCase();
      const discountType: Coupon['discountType'] = typeRaw.includes('percent') || typeRaw === 'percentage' ? 'percentage' : 'flat';
      return {
        id: String(row?.id ?? ''),
        restaurantId: String(row?.restaurant_id ?? row?.restaurantId ?? selectedRestaurant.id ?? ''),
        code: String(row?.code ?? row?.name ?? '').trim(),
        description: String(row?.description ?? '').trim(),
        discountType,
        discountValue: toNumberSafe(row?.discount_value ?? row?.discountValue, 0),
        minOrderValue: toNumberSafe(row?.min_order_value ?? row?.minOrderValue, 0),
        maxDiscount:
          row?.max_discount_amount !== undefined && row?.max_discount_amount !== null
            ? toNumberSafe(row.max_discount_amount, 0)
            : row?.max_discount !== undefined && row?.max_discount !== null
              ? toNumberSafe(row.max_discount, 0)
              : row?.maxDiscount !== undefined && row?.maxDiscount !== null
                ? toNumberSafe(row.maxDiscount, 0)
                : undefined,
        isActive: Boolean(row?.is_active ?? row?.isActive ?? true),
        validFrom: String(row?.valid_from ?? row?.validFrom ?? ''),
        validUntil: String(row?.valid_until ?? row?.validUntil ?? ''),
        usageLimit:
          row?.usage_limit !== undefined && row?.usage_limit !== null
            ? toNumberSafe(row.usage_limit, 0)
            : row?.max_uses !== undefined && row?.max_uses !== null
              ? toNumberSafe(row.max_uses, 0)
              : row?.usageLimit !== undefined && row?.usageLimit !== null
                ? toNumberSafe(row.usageLimit, 0)
                : undefined,
        usedCount: toNumberSafe(row?.current_uses ?? row?.used_count ?? row?.usedCount ?? 0, 0),
        calculatedDiscount:
          row?.calculatedDiscount !== undefined && row?.calculatedDiscount !== null
            ? toNumberSafe(row.calculatedDiscount, 0)
            : row?.calculated_discount !== undefined && row?.calculated_discount !== null
              ? toNumberSafe(row.calculated_discount, 0)
              : undefined,
      };
    };

    const loadCoupons = async () => {
      try {
        const response: any = await api.coupons.getByRestaurant(String(selectedRestaurant.id), true);
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        const normalized = (rows as any[])
          .map((row) => normalizeCoupon(row))
          .filter((coupon) => Boolean(coupon.id) && Boolean(coupon.code));
        if (!mounted) return;
        setRestaurantCoupons(normalized);
      } catch {
        if (!mounted) return;
        setRestaurantCoupons([]);
      }
    };

    void loadCoupons();
    return () => {
      mounted = false;
    };
  }, [selectedRestaurant?.id]);

  useEffect(() => {
    let mounted = true;

    const toBooleanSafe = (value: unknown, fallback = true): boolean => {
      if (value === null || value === undefined) return fallback;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value !== 0;
      if (typeof value === 'bigint') return value !== BigInt(0);
      const raw = String(value).trim().toLowerCase();
      if (!raw) return fallback;
      if (raw === '0' || raw === 'false' || raw === 'no' || raw === 'n') return false;
      if (raw === '1' || raw === 'true' || raw === 'yes' || raw === 'y') return true;
      return fallback;
    };

    const toNumberSafe = (value: unknown, fallback = 0): number => {
      if (value === null || value === undefined) return fallback;
      if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
      if (typeof value === 'bigint') return Number(value);
      const raw =
        typeof value === 'string'
          ? value
          : typeof (value as any)?.toString === 'function'
          ? (value as any).toString()
          : String(value);
      const parsed = Number(String(raw).trim());
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const normalizeOffer = (row: any): Coupon => {
      const applicableModeRaw = String(row?.applicable_mode ?? row?.applicableMode ?? 'ALL').trim().toUpperCase();
      const applicableMode: 'all' | 'categories' | 'items' =
        applicableModeRaw === 'CATEGORIES' ? 'categories' : applicableModeRaw === 'ITEMS' ? 'items' : 'all';

      return {
        id: String(row?.id ?? ''),
        restaurantId: String(row?.restaurant_id ?? row?.restaurantId ?? selectedRestaurant?.id ?? ''),
        code: String(row?.code ?? ''),
        description: String(row?.description ?? row?.name ?? ''),
        discountType: String(row?.discount_type ?? row?.discountType) === 'flat' ? 'flat' : 'percentage',
        discountValue: toNumberSafe(row?.discount_value ?? row?.discountValue ?? 0, 0),
        minOrderValue: toNumberSafe(row?.min_order_value ?? row?.minOrderValue ?? 0, 0),
        maxDiscount:
          row?.max_discount_amount !== undefined && row?.max_discount_amount !== null
            ? toNumberSafe(row.max_discount_amount, 0)
            : row?.max_discount !== undefined && row?.max_discount !== null
              ? toNumberSafe(row.max_discount, 0)
              : row?.maxDiscount !== undefined && row?.maxDiscount !== null
                ? toNumberSafe(row.maxDiscount, 0)
                : undefined,
        isActive: toBooleanSafe(row?.is_active ?? row?.isActive, true),
        validFrom: String(row?.valid_from ?? row?.validFrom ?? ''),
        validUntil: String(row?.valid_until ?? row?.validUntil ?? ''),
        usageLimit:
          row?.max_uses !== undefined && row?.max_uses !== null
            ? toNumberSafe(row.max_uses, 0)
            : row?.usage_limit !== undefined && row?.usage_limit !== null
              ? toNumberSafe(row.usage_limit, 0)
              : row?.usageLimit !== undefined && row?.usageLimit !== null
                ? toNumberSafe(row.usageLimit, 0)
                : undefined,
        usedCount: toNumberSafe(row?.current_uses ?? row?.used_count ?? row?.usedCount ?? 0, 0),
        applicableMode,
        selectedCategoryIds: parseJsonArray<string>(
          row?.applicable_category_ids ?? row?.applicableCategoryIds ?? []
        ).map((v) => String(v)),
        selectedItemIds: parseJsonArray<string>(row?.applicable_item_ids ?? row?.applicableItemIds ?? []).map((v) =>
          String(v)
        ),
      };
    };

    const loadOffers = async () => {
      if (!selectedRestaurant?.id) {
        setRestaurantOffers([]);
        setSelectedOffer(null);
        return;
      }
      try {
        const response: any = await api.offers.getByRestaurant(String(selectedRestaurant.id), false);
        const rows = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        const normalized = (rows as any[])
          .map((row) => normalizeOffer(row))
          .filter((offer) => Boolean(offer.id) && Boolean(offer.code));
        if (!mounted) return;
        setRestaurantOffers(normalized);
        // Reset selection when restaurant changes or selected offer no longer exists.
        setSelectedOffer((prev) => (prev && normalized.some((o) => o.id === prev.id) ? prev : null));
      } catch {
        if (!mounted) return;
        setRestaurantOffers([]);
        setSelectedOffer(null);
      }
    };

    void loadOffers();
    return () => {
      mounted = false;
    };
  }, [selectedRestaurant?.id]);

  const restaurantOfferMeta = useMemo(() => {
    const activeToday = (Array.isArray(restaurantCoupons) ? restaurantCoupons : [])
      .filter((c) => Number(c.discountValue || 0) > 0)
      .filter(isCouponActiveToday);
    if (activeToday.length === 0) return null;

    const score = (coupon: Coupon) => (coupon.discountType === 'percentage' ? Number(coupon.discountValue || 0) * 1000 : Number(coupon.discountValue || 0));
    const best = [...activeToday].sort((a, b) => score(b) - score(a))[0];
    if (!best) return null;

    const label =
      best.discountType === 'percentage'
        ? `${Math.round(Number(best.discountValue || 0))}% OFF`
        : `${formatMoney(Number(best.discountValue || 0))} OFF`;
    const tone: 'orange' | 'green' | 'red' =
      best.discountType === 'flat' ? 'red' : Number(best.discountValue || 0) >= 20 ? 'orange' : 'green';
    return { badge: label, tone, code: best.code };
  }, [restaurantCoupons, formatMoney]);

  const activeRestaurantOffers = (Array.isArray(restaurantOffers) ? restaurantOffers : [])
    .filter((o) => Number(o.discountValue || 0) > 0)
    .filter(isCouponActiveToday);

  useEffect(() => {
    if (!selectedOffer) return;
    if (activeRestaurantOffers.some((o) => String(o.id) === String(selectedOffer.id))) return;
    setSelectedOffer(null);
  }, [activeRestaurantOffers, selectedOffer]);

  const offerChipLabel = (offer: Coupon): string => {
    const value = Number(offer.discountValue || 0);
    if (!Number.isFinite(value) || value <= 0) return String(offer.code || 'Offer').trim() || 'Offer';
    return offer.discountType === 'percentage' ? `${Math.round(value)}% OFF` : `${formatMoney(value)} OFF`;
  };

  useEffect(() => {
    if (selectedRestaurant) return;
    setMenuRedirectUrlActive(false);
    if (!keyFromUrl) {
      clearPublicMenuKeyFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant?.id]);


  useEffect(() => {
    if (!keyFromUrl) return;
    if (selectedRestaurant) return;

    // Open the restaurant directly when the tab is loaded on `/<public_menu_key>`.
    setPublicKeyBootLoading(true);
    setMenuRedirectUrlActive(true);
    setOrderMode('order-online');
    if (!hasSelectedLocation) {
      setHasSelectedLocation(true);
      if (!selectedLocation) setSelectedLocation('Current Location');
    }

    let mounted = true;
    const load = async () => {
      try {
        const response: any = await api.restaurants.getByPublicKey(keyFromUrl);
        const payload = response?.data ?? response?.data?.data ?? null;
        if (mounted && payload?.id) {
          setSelectedRestaurant(normalizeRestaurantFromApi(payload));
        }
      } catch {
        // If the key is invalid, keep the app usable by returning to the root URL.
        clearPublicMenuKeyFromUrl();
      } finally {
        if (mounted) setPublicKeyBootLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyFromUrl]);

  // Location selection states (boot from landing page auto-location)
  const bootLocationLabel = (() => {
    try {
      return String(localStorage.getItem('customer_location_label') || '').trim() || null;
    } catch {
      return null;
    }
  })();
  const bootHasCoords = (() => {
    try {
      const lat = Number(localStorage.getItem('customer_latitude'));
      const lng = Number(localStorage.getItem('customer_longitude'));
      return Number.isFinite(lat) && Number.isFinite(lng);
    } catch {
      return false;
    }
  })();

  const [hasSelectedLocation, setHasSelectedLocation] = useState(Boolean(bootLocationLabel) || bootHasCoords);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(
    bootLocationLabel || (bootHasCoords ? 'Current Location' : null),
  );
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [, setAutoDetectingLocation] = useState(true);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'order-online' | 'offers' | 'reviews' | 'photos' | 'menu'>('order-online');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const effectiveSelectedRestaurant = selectedRestaurant
    ? {
        ...selectedRestaurant,
        ...(restaurantRatingOverrides[selectedRestaurant.id] || {}),
      }
    : null;

  const pricingRestaurant = effectiveSelectedRestaurant || selectedRestaurant;

  const headerDescriptionText = String((effectiveSelectedRestaurant || selectedRestaurant)?.description || '').trim();
  const headerAddressText = (() => {
    const source: any = effectiveSelectedRestaurant || selectedRestaurant;
    const parts = [source?.address, source?.city, source?.state]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    const joined = parts.join(', ');
    if (joined) return joined;
    return String(source?.location || source?.area || '').trim();
  })();

  useEffect(() => {
    if (!headerDescriptionText || isHeaderDescriptionExpanded) return;

    const check = () => {
      const el = headerDescriptionRef.current;
      if (!el) return;
      if (el.clientHeight <= 0) return;
      setIsHeaderDescriptionOverflowing(el.scrollHeight > el.clientHeight + 1);
    };

    check();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(check);
      if (headerDescriptionRef.current) observer.observe(headerDescriptionRef.current);
    }
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
      observer?.disconnect();
    };
  }, [headerDescriptionText, isHeaderDescriptionExpanded]);

  const integrationSetup =
    (effectiveSelectedRestaurant as any)?.integrationSetup ?? (effectiveSelectedRestaurant as any)?.integration_setup ?? null;
  const integrationScenariosRaw: any[] = Array.isArray(integrationSetup?.scenarios) ? integrationSetup.scenarios : [];
  const integrationScenarios: string[] = integrationScenariosRaw
    .map((scenario) => String(scenario || '').trim().toLowerCase())
    .filter(Boolean);
  const hasMenuRedirect = integrationScenarios.includes('menu_redirect');
  // Treat `menu_redirect` as still orderable in-app; it only affects the URL behavior.
  const hasFullOrdering =
    integrationScenarios.includes('full_ordering') ||
    integrationScenarios.includes('menu_redirect') ||
    integrationScenarios.length === 0; // default legacy behavior
  const isMenuRedirect = Boolean(selectedRestaurant && hasMenuRedirect);
  const canOrderInApp = Boolean(selectedRestaurant && hasFullOrdering);

  useEffect(() => {
    let mounted = true;

    const restaurantId = String(selectedRestaurant?.id || '').trim();
    if (!restaurantId) {
      setResolvedPublicMenuKey('');
      return () => {
        mounted = false;
      };
    }

    const existingKey = String(
      (effectiveSelectedRestaurant as any)?.publicMenuKey ??
        (effectiveSelectedRestaurant as any)?.public_menu_key ??
        (selectedRestaurant as any)?.publicMenuKey ??
        (selectedRestaurant as any)?.public_menu_key ??
        ''
    ).trim();
    setResolvedPublicMenuKey(existingKey);

    const resolveFromApi = async () => {
      try {
        const response: any = await api.restaurants.getById(restaurantId);
        const payload = response?.data ?? response?.data?.data ?? null;
        const key = String(payload?.publicMenuKey ?? payload?.public_menu_key ?? '').trim();
        if (mounted) {
          if (key) setResolvedPublicMenuKey(key);
          if (payload?.id) {
            setSelectedRestaurant((prev: any) => {
              if (!prev || String(prev.id || '').trim() !== restaurantId) return prev;
              const merged = normalizeRestaurantFromApi(payload, prev);
              if (!merged) return prev;
              if (prev?.distance !== undefined && prev?.distance !== null && (payload as any)?.distance == null) {
                (merged as any).distance = prev.distance;
              }
              return merged;
            });
          }
        }
      } catch {
        // ignore
      }
    };

    void resolveFromApi();

    return () => {
      mounted = false;
    };
  }, [selectedRestaurant?.id]);

  function normalizeRestaurantFromApi(raw: any, previous: Restaurant | null = null): Restaurant | null {
    if (!raw) return previous;
    const coerceNumber = (value: any) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const match = value.match(/-?\d+(?:\.\d+)?/);
        return match ? Number(match[0]) : Number(value);
      }
      return Number(value);
    };

    const rating = coerceNumber(
      raw?.rating ??
        raw?.avgRating ??
        raw?.averageRating ??
        raw?.average_rating ??
        raw?.avg_rating ??
        raw?.avg_rating_value ??
        raw?.avgRatingValue
    );
    const totalRatings = coerceNumber(
      raw?.totalRatings ??
        raw?.total_ratings ??
        raw?.ratingCount ??
        raw?.rating_count ??
        raw?.reviewCount ??
        raw?.review_count ??
        raw?.totalReviews ??
        raw?.total_reviews
    );

    const merged: any = previous ? { ...previous, ...raw } : { ...raw };
    if (Number.isFinite(rating)) merged.rating = rating;
    if (Number.isFinite(totalRatings)) merged.totalRatings = Math.max(0, Math.floor(totalRatings));
    return merged as Restaurant;
  }

  const toRestaurantDomainSegment = (raw: unknown): string => {
    const cleaned = String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/.*$/, '')
      .replace(/\.[a-z]{2,}$/, '');
    const base = cleaned || String(raw || '').trim().toLowerCase();
    return base
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/_+/g, '_');
  };

  const navigateToPublicMenu = (intentItemId?: string) => {
    const key = String(resolvedPublicMenuKey || '').trim().toLowerCase();
    if (!/^[a-f0-9]{16}$/i.test(key)) {
      warning('Public menu key not configured for this restaurant.');
      return;
    }

    if (intentItemId) {
      try {
        sessionStorage.setItem('public_menu_intent_item_id', String(intentItemId));
      } catch {
        // ignore
      }
    }

    // Open the menu-redirect URL in a new tab (keep current ordering flow on this page).
    if (!menuRedirectUrlActive) {
      const websiteUrl =
        (integrationSetup as any)?.menuRedirect?.websiteUrl ??
        (integrationSetup as any)?.menu_redirect?.websiteUrl ??
        (integrationSetup as any)?.menu_redirect?.website_url ??
        (selectedRestaurant as any)?.slug ??
        (selectedRestaurant as any)?.name ??
        '';
      const restaurantSegment = toRestaurantDomainSegment(websiteUrl);
      const path = restaurantSegment ? `/${encodeURIComponent(restaurantSegment)}/${encodeURIComponent(key)}` : `/${encodeURIComponent(key)}`;
      const targetUrl = `${window.location.origin}${path}`;
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
      setMenuRedirectUrlActive(true);
    }
  };

  useEffect(() => {
    // When opening a restaurant, keep the URL clean unless the user explicitly triggered menu-redirect via ADD.
    if (!selectedRestaurant?.id) return;
    if (menuRedirectUrlActive) return;
    clearPublicMenuKeyFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRestaurant?.id, menuRedirectUrlActive]);

  // Sync dietary filter with ProfileDropdown changes
  useEffect(() => {
    if (!user.address) return;
    setSelectedLocation(`Home: ${user.address}`);
    setHasSelectedLocation(true);
    setAutoDetectingLocation(false);
  }, [user.address]);

  useEffect(() => {
    const handleDietaryModeChange = (event: CustomEvent) => {
      setDietaryFilter(String((event as any)?.detail ?? 'all'));
    };

    // Listen for custom dietary mode change events
    window.addEventListener('dietaryModeChange', handleDietaryModeChange as EventListener);

    return () => {
      window.removeEventListener('dietaryModeChange', handleDietaryModeChange as EventListener);
    };
  }, []);

  // Mock saved addresses
  const savedAddresses: SavedAddress[] = [
    {
      id: '1',
      type: 'Home',
      address: '123 Residency Road, MG Road, Bangalore',
      landmark: 'Near City Mall',
      isPrimary: true
    },
    {
      id: '2',
      type: 'Work',
      address: '456 Tech Park, Whitefield, Bangalore',
      landmark: 'Building A, 5th Floor'
    },
    {
      id: '3',
      type: 'Other',
      address: '789 Koramangala 4th Block, Bangalore',
      landmark: 'Opposite Coffee Shop'
    }
  ];

  const handleUseCurrentLocation = () => {
    setDetectingLocation(true);
    
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      setDetectingLocation(false);
      return;
    }

    // Request current position
    navigator.geolocation.getCurrentPosition(
      // Success callback
      (position) => {
        const { latitude, longitude } = position.coords;
        
        // Use reverse geocoding service (for demo, we'll use a mock location based on coordinates)
        // In production, you'd use Google Maps Geocoding API or similar
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
          .then(response => response.json())
          .then(data => {
            const address = data.display_name || `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`;
            setSelectedLocation(`Current Location: ${address}`);
            setDetectingLocation(false);
            setHasSelectedLocation(true);
          })
          .catch(() => {
            // Fallback if geocoding fails
            setSelectedLocation(`Current Location: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
            setDetectingLocation(false);
            setHasSelectedLocation(true);
          });
      },
      // Error callback
      (error) => {
        setDetectingLocation(false);
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access denied. Please enable location permissions in your browser settings to use this feature.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out. Please try again.');
            break;
          default:
            alert('An unknown error occurred while getting your location.');
            break;
        }
      },
      // Options
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSelectAddress = (address: SavedAddress) => {
    setSelectedLocation(`${address.type}: ${address.address}`);
    setHasSelectedLocation(true);
  };

  useEffect(() => {
    const storedLocation = localStorage.getItem('customer_selected_location');
    if (storedLocation) {
      setSelectedLocation(storedLocation);
      setHasSelectedLocation(true);
    }
    setAutoDetectingLocation(false);
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    localStorage.setItem('customer_selected_location', selectedLocation);
  }, [selectedLocation]);

  const loadStoredCart = (restaurantId: string) => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const items = Array.isArray(parsed?.[restaurantId]) ? parsed[restaurantId] : [];
      const nextMap = new Map<string, CartItem>();
      items.forEach((item: CartItem) => {
        if (item?.cartKey) {
          nextMap.set(item.cartKey, item);
        }
      });
      setCart(nextMap);
      setActiveCartRestaurantId(restaurantId);
    } catch {
      setCart(new Map());
      setActiveCartRestaurantId(restaurantId);
    }
  };

  useEffect(() => {
    if (!selectedRestaurant?.id) return;
    lastRestaurantRef.current = selectedRestaurant;
    loadStoredCart(selectedRestaurant.id);
  }, [selectedRestaurant?.id]);

  useEffect(() => {
    if (!activeCartRestaurantId) return;
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      if (cart.size > 0) {
        parsed[activeCartRestaurantId] = Array.from(cart.values());
      } else {
        delete parsed[activeCartRestaurantId];
      }
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // ignore storage errors
    }
  }, [cart, activeCartRestaurantId]);

  useEffect(() => {
    if (openCartOnRestaurantLoad && selectedRestaurant) {
      setShowCart(true);
      setOpenCartOnRestaurantLoad(false);
    }
  }, [openCartOnRestaurantLoad, selectedRestaurant]);

  useEffect(() => {
    let mounted = true;

    const loadRestaurantMenu = async () => {
      if (!selectedRestaurant?.id) {
        if (mounted) {
          setMenuItems([]);
          setCategories([]);
        }
        return;
      }

      setMenuLoading(true);
      try {
        const [categoriesRes, itemsRes] = await Promise.allSettled([
          api.menu.getCategories(selectedRestaurant.id),
          api.menu.getItems(selectedRestaurant.id),
        ]);

        if (!mounted) return;

        const mappedCategories: Category[] =
          categoriesRes.status === 'fulfilled'
            ? (Array.isArray((categoriesRes.value as any)?.data) ? (categoriesRes.value as any).data : [])
                .map((item: any, index: number) => ({
                  id: String(item?.id || '').trim(),
                  restaurantId: selectedRestaurant.id,
                  name: String(item?.name || item?.food_category?.name || '').trim(),
                  image: item?.image || undefined,
                  order: Number(item?.display_order ?? item?.displayOrder ?? index),
                  isActive: Boolean(item?.is_active ?? item?.isActive ?? true),
                  foodCategoryId: String(
                    item?.food_category_id ?? item?.foodCategoryId ?? item?.food_category?.id ?? ''
                  ).trim() || undefined,
                }))
                .filter((item: Category) => item.id && item.name && item.isActive)
            : [];

        const mappedItems: MenuItem[] =
          itemsRes.status === 'fulfilled'
            ? (Array.isArray((itemsRes.value as any)?.data) ? (itemsRes.value as any).data : [])
                .map((item: any) => ({
                  id: String(item?.id || '').trim(),
                  restaurantId: selectedRestaurant.id,
                  categoryId: String(item?.categoryId || item?.category_id || item?.category?.id || '').trim(),
                  name: String(item?.name || '').trim(),
                  description: String(item?.description || '').trim(),
                  price: Number(item?.price ?? 0),
                  rating: (() => {
                    const raw =
                      item?.rating ??
                      item?.avgRating ??
                      item?.averageRating ??
                      item?.average_rating ??
                      item?.avg_rating ??
                      item?.itemRating ??
                      item?.item_rating;
                    if (typeof raw === 'number') return raw;
                    if (typeof raw === 'string') {
                      const match = raw.match(/-?\d+(?:\.\d+)?/);
                      return match ? Number(match[0]) : Number(raw);
                    }
                    const n = Number(raw);
                    return Number.isFinite(n) ? n : undefined;
                  })(),
                  ratingCount: (() => {
                    const raw =
                      item?.ratingCount ??
                      item?.reviewCount ??
                      item?.totalReviews ??
                      item?.total_reviews ??
                      item?.rating_count ??
                      item?.review_count;
                    if (typeof raw === 'number') return raw;
                    if (typeof raw === 'string') {
                      const match = raw.match(/-?\d+(?:\.\d+)?/);
                      return match ? Number(match[0]) : Number(raw);
                    }
                    const n = Number(raw);
                    return Number.isFinite(n) ? n : undefined;
                  })(),
                  discountPrice:
                    item?.discountPrice !== undefined && item?.discountPrice !== null && String(item.discountPrice) !== ''
                      ? Number(item.discountPrice)
                      : item?.discount_price !== undefined && item?.discount_price !== null && String(item.discount_price) !== ''
                        ? Number(item.discount_price)
                        : null,
                  discountPercent: (() => {
                    const base = Number(item?.price ?? 0);
                    const discounted =
                      item?.discountPrice !== undefined && item?.discountPrice !== null && String(item.discountPrice) !== ''
                        ? Number(item.discountPrice)
                        : item?.discount_price !== undefined && item?.discount_price !== null && String(item.discount_price) !== ''
                          ? Number(item.discount_price)
                          : null;
                    if (!Number.isFinite(base) || base <= 0) return null;
                    if (!Number.isFinite(Number(discounted)) || discounted === null) return null;
                    const disc = Number(discounted);
                    if (disc <= 0 || disc >= base) return null;
                    const pct = ((base - disc) / base) * 100;
                    return pct > 0 ? Math.round(pct) : null;
                  })(),
                  image: item?.image || undefined,
                  tags: (() => {
                    const rawTags = Array.isArray(item?.tags) ? item.tags : [];
                    const normalized = rawTags
                      .map((tag: any) => normalizeDietaryTag(tag))
                      .filter(Boolean);

                    const isVeg = item?.isVeg ?? item?.is_veg;
                    if (typeof isVeg === 'boolean') {
                      const inferred = isVeg ? 'veg' : 'non-veg';
                      if (!normalized.includes('veg') && !normalized.includes('non-veg')) {
                        normalized.push(inferred);
                      }
                    }

                    const unique = Array.from(new Set(normalized));
                    return unique;
                  })(),
                  spicyLevel: Number(item?.spicy_level ?? item?.spicyLevel ?? 0),
                  isActive: Boolean(item?.is_active ?? item?.isActive ?? true),
                  preparationTime: Number(item?.preparation_time ?? item?.preparationTime ?? 20),
                  servingSize: (() => {
                    const rawServing = item?.servingSize ?? item?.serving_size;
                    return formatServingSizeLabel(rawServing);
                  })(),
                  inStock: Boolean(item?.in_stock ?? item?.inStock ?? item?.isAvailable ?? true),
                  variants: parseJsonArray<any>(item?.variants).map((variant: any) => ({
                        id: String(variant?.id || ''),
                        name: String(variant?.name || ''),
                        price: Number(variant?.price || 0),
                        isDefault: Boolean(variant?.isDefault || variant?.is_default || false),
                      })),
                  addOns: parseJsonArray<any>(item?.addOns ?? item?.addons).map((addon: any) => ({
                        id: String(addon?.id || ''),
                        name: String(addon?.name || ''),
                        price: Number(addon?.price || 0),
                        category: String(addon?.category || 'addon'),
                        source: 'addon' as const,
                      })),
                  customizationGroups: parseJsonArray<any>(
                    item?.customizationGroups ?? item?.customization_groups ?? (item as any)?.['customization_groups']
                  ).map(
                    (group: any) => ({
                      id: String(group?.id || ''),
                      name: String(group?.name || ''),
                      displayName: String(group?.displayName || group?.group_display_name || group?.name || ''),
                      inputType:
                        String(group?.inputType || group?.group_input_type || 'multi') === 'single' ? 'single' : 'multi',
                      isRequired: Boolean(group?.isRequired ?? group?.is_required ?? false),
                      minSelect: Number(group?.minSelect ?? group?.min_select ?? 0),
                      maxSelect:
                        group?.maxSelect === null || group?.max_select === null || group?.maxSelect === ''
                          ? null
                          : Number(group?.maxSelect ?? group?.max_select ?? 0),
                      sortOrder: Number(group?.sortOrder ?? group?.sort_order ?? 0),
                      options: parseJsonArray<any>(group?.options).map((option: any) => ({
                        id: String(option?.id || ''),
                        name: String(option?.name || ''),
                        price: Number(option?.price || 0),
                        isDefault: Boolean(option?.isDefault || option?.is_default || false),
                        sortOrder: Number(option?.sortOrder ?? option?.sort_order ?? 0),
                        category: String(group?.displayName || group?.name || 'Customization'),
                      })),
                    })
                  ),
                }))
                .filter((item: MenuItem) => item.id && item.categoryId && item.name)
            : [];

        setCategories(mappedCategories.sort((a, b) => a.order - b.order));
        setMenuItems(mappedItems);
      } catch {
        if (mounted) {
          setMenuItems([]);
          setCategories([]);
        }
      } finally {
        if (mounted) setMenuLoading(false);
      }
    };

    void loadRestaurantMenu();
    return () => {
      mounted = false;
    };
  }, [selectedRestaurant?.id]);

  // Reset to "All Items" when restaurant is first selected
  useEffect(() => {
    if (selectedRestaurant) {
      setSelectedCategory(null); // Show all items by default
    }
  }, [selectedRestaurant?.id]); // Only run when restaurant changes

  // Check for active orders on component mount (skip for guests)
  useEffect(() => {
    if (isGuestUser) return;
    let mounted = true;

    const loadActiveOrder = async () => {
      try {
        const response = await api.orders.getMyOrders({ page: 1, limit: 20 });
        const orders = Array.isArray((response as any)?.data?.orders) ? (response as any).data.orders : [];
        const activeOrder = orders.find((order: any) => {
          const status = String(order?.status || '').toUpperCase();
          return !['DELIVERED', 'COMPLETED', 'CANCELLED'].includes(status);
        });

        if (mounted && activeOrder?.id) {
          setActiveOrderId(String(activeOrder.id));
          setShowBottomTracker(true);
        }
      } catch {
        // ignore
      }
    };

    void loadActiveOrder();
    return () => {
      mounted = false;
    };
  }, [isGuestUser]);

  // Generate unique cart key
  const generateCartKey = (
    item: MenuItem,
    variant: MenuItemVariant | null,
    addOns: MenuItemAddOn[],
    instructions: string
  ): string => {
    const variantId = variant?.id || 'no-variant';
    const addOnIds = addOns.map(a => a.id).sort().join(',');
    return `${item.id}-${variantId}-${addOnIds}-${instructions}`;
  };

  const handleAddToCart = (
    item: MenuItem,
    selectedVariant: MenuItemVariant | null,
    selectedAddOns: MenuItemAddOn[],
    specialInstructions: string
  ) => {
    const cartKey = generateCartKey(item, selectedVariant, selectedAddOns, specialInstructions);
    const newCart = new Map(cart);
    const existing = newCart.get(cartKey);

    if (existing) {
      newCart.set(cartKey, { ...existing, quantity: existing.quantity + 1 });
    } else {
      newCart.set(cartKey, {
        item,
        quantity: 1,
        selectedVariant,
        selectedAddOns,
        specialInstructions,
        cartKey
      });
    }
    setCart(newCart);
  };

  const handleQuickAddToCart = (item: MenuItem) => {
    // Always open customization modal for better UX
    setCustomizingItem(item);

    const hasCustomizations = (item.customizationGroups && item.customizationGroups.length > 0) || false;
    if (hasCustomizations) return;

    void (async () => {
      try {
        const response = await api.menu.getItemById(item.id);
        const raw = (response as any)?.data;
        if (!raw) return;

        const mappedItem: MenuItem = {
          ...item,
          variants: parseJsonArray<any>(raw?.variants).map((variant: any) => ({
            id: String(variant?.id || ''),
            name: String(variant?.name || ''),
            price: Number(variant?.price || 0),
            isDefault: Boolean(variant?.isDefault || variant?.is_default || false),
          })),
          addOns: parseJsonArray<any>(raw?.addOns ?? raw?.addons).map((addon: any) => ({
            id: String(addon?.id || ''),
            name: String(addon?.name || ''),
            price: Number(addon?.price || 0),
            category: String(addon?.category || 'addon'),
            source: 'addon' as const,
          })),
          customizationGroups: parseJsonArray<any>(
            raw?.customizationGroups ?? raw?.customization_groups ?? (raw as any)?.['customization_groups']
          ).map(
            (group: any) => ({
              id: String(group?.id || ''),
              name: String(group?.name || ''),
              displayName: String(group?.displayName || group?.group_display_name || group?.name || ''),
              inputType:
                String(group?.inputType || group?.group_input_type || 'multi') === 'single' ? 'single' : 'multi',
              isRequired: Boolean(group?.isRequired ?? group?.is_required ?? false),
              minSelect: Number(group?.minSelect ?? group?.min_select ?? 0),
              maxSelect:
                group?.maxSelect === null || group?.max_select === null || group?.maxSelect === ''
                  ? null
                  : Number(group?.maxSelect ?? group?.max_select ?? 0),
              sortOrder: Number(group?.sortOrder ?? group?.sort_order ?? 0),
              options: parseJsonArray<any>(group?.options).map((option: any) => ({
                id: String(option?.id || ''),
                name: String(option?.name || ''),
                price: Number(option?.price || 0),
                isDefault: Boolean(option?.isDefault || option?.is_default || false),
                sortOrder: Number(option?.sortOrder ?? option?.sort_order ?? 0),
                category: String(group?.displayName || group?.name || 'Customization'),
              })),
            })
          ),
        };

        setCustomizingItem(mappedItem);
      } catch {
        // Ignore: fallback to already loaded item.
      }
    })();
  };

  const incrementCartItem = (cartKey: string) => {
    const newCart = new Map(cart);
    const existing = newCart.get(cartKey);
    if (existing) {
      newCart.set(cartKey, { ...existing, quantity: existing.quantity + 1 });
      setCart(newCart);
    }
  };

  const decrementCartItem = (cartKey: string) => {
    const newCart = new Map(cart);
    const existing = newCart.get(cartKey);
    if (existing) {
      if (existing.quantity > 1) {
        newCart.set(cartKey, { ...existing, quantity: existing.quantity - 1 });
      } else {
        newCart.delete(cartKey);
      }
      setCart(newCart);
    }
  };

  const getItemTotalInCart = (itemId: string): number => {
    let total = 0;
    cart.forEach(cartItem => {
      if (cartItem.item.id === itemId) {
        total += cartItem.quantity;
      }
    });
    return total;
  };

  const applyCouponByCode = async (
    rawCode: string
  ): Promise<{ ok: true } | { ok: false; message: string } | null> => {
    if (!selectedRestaurant) return null;
    const normalizedCode = String(rawCode || '').trim().toUpperCase();
    if (!normalizedCode) return null;

    const normalizeCouponResponse = (coupon: any): Coupon => ({
      id: String(coupon?.id ?? ''),
      restaurantId: String(coupon?.restaurant_id ?? coupon?.restaurantId ?? selectedRestaurant.id ?? ''),
      code: String(coupon?.code ?? normalizedCode),
      description: String(coupon?.description ?? ''),
      discountType:
        String(coupon?.discount_type ?? coupon?.discountType ?? 'percentage').toLowerCase() === 'flat'
          ? 'flat'
          : 'percentage',
      discountValue: Number(coupon?.discount_value ?? coupon?.discountValue ?? 0),
      minOrderValue: Number(coupon?.min_order_value ?? coupon?.minOrderValue ?? 0),
      maxDiscount:
        coupon?.max_discount_amount !== undefined && coupon?.max_discount_amount !== null
          ? Number(coupon.max_discount_amount)
          : coupon?.max_discount !== undefined && coupon?.max_discount !== null
            ? Number(coupon.max_discount)
            : coupon?.maxDiscount !== undefined && coupon?.maxDiscount !== null
              ? Number(coupon.maxDiscount)
              : undefined,
      isActive: Boolean(coupon?.is_active ?? coupon?.isActive ?? true),
      validFrom: String(coupon?.valid_from ?? coupon?.validFrom ?? new Date().toISOString()),
      validUntil: String(coupon?.valid_until ?? coupon?.validUntil ?? new Date().toISOString()),
      usageLimit:
        coupon?.max_uses !== undefined && coupon?.max_uses !== null
          ? Number(coupon.max_uses)
          : coupon?.usage_limit !== undefined && coupon?.usage_limit !== null
            ? Number(coupon.usage_limit)
            : coupon?.usageLimit !== undefined && coupon?.usageLimit !== null
              ? Number(coupon.usageLimit)
              : undefined,
      usedCount: Number(coupon?.current_uses ?? coupon?.used_count ?? coupon?.usedCount ?? 0),
      calculatedDiscount:
        coupon?.calculatedDiscount !== undefined && coupon?.calculatedDiscount !== null
          ? Number(coupon.calculatedDiscount)
          : coupon?.calculated_discount !== undefined && coupon?.calculated_discount !== null
            ? Number(coupon.calculated_discount)
            : undefined,
    });

    try {
      setCouponCode(normalizedCode);
      const coupon = await couponDataService.validate(normalizedCode, selectedRestaurant.id, subtotal);
      setAppliedCoupon(normalizeCouponResponse(coupon));
      setCouponError('');
      return { ok: true };
    } catch (caught: any) {
      setAppliedCoupon(null);
      const message = caught?.message || 'Invalid coupon';
      setCouponError(message);
      return { ok: false, message };
    }
  };

  const applyCoupon = async () => {
    await applyCouponByCode(couponCode);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  // Calculate order totals
  const cartItems = Array.from(cart.values());
  const cartItemCount = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const toNumberSafe = (value: unknown, fallback = 0): number => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
    if (typeof value === 'bigint') return Number(value);
    const raw =
      typeof value === 'string'
        ? value
        : typeof (value as any)?.toString === 'function'
        ? (value as any).toString()
        : String(value);
    const parsed = Number(String(raw).trim());
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const subtotal = cartItems.reduce((sum, cartItem) => {
    const basePrice = cartItem.selectedVariant?.price || getEffectiveItemPrice(cartItem.item);
    const addOnsPrice = cartItem.selectedAddOns.reduce((sum, addOn) => sum + addOn.price, 0);
    return sum + (basePrice + addOnsPrice) * cartItem.quantity;
  }, 0);
  
  const freeDeliveryAbove = toNumberSafe(pricingRestaurant?.freeDeliveryAbove, 0);
  const hasFreeDeliveryThreshold = freeDeliveryAbove > 0;
  const isFreeDeliveryEligible = hasFreeDeliveryThreshold && subtotal >= freeDeliveryAbove;
  const baseDeliveryFee = toNumberSafe(pricingRestaurant?.deliveryFee, 0);
  const deliveryFee = isFreeDeliveryEligible ? 0 : baseDeliveryFee;
  
  const packagingCharges = toNumberSafe(pricingRestaurant?.packagingCharges, 0);
  
  const discount = appliedCoupon && pricingRestaurant 
    ? Number(appliedCoupon.calculatedDiscount ?? calculateDiscount(appliedCoupon, subtotal))
    : 0;
  
  const taxableAmount = subtotal - discount + deliveryFee + packagingCharges;
  const taxRate = toNumberSafe(pricingRestaurant?.taxRate, 5) || 5;
  const tax = Math.round((taxableAmount * taxRate) / 100);
  
  const grandTotal = subtotal + deliveryFee + packagingCharges + tax - discount;

  const formatCompactCount = (count: number) => {
    if (!Number.isFinite(count) || count <= 0) return '';
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\\.0$/, '')}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\\.0$/, '')}K`;
    return String(Math.round(count));
  };

  const normalizeDietaryTag = (raw: unknown): string =>
    String(raw || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/^nonveg$/, 'non-veg');

  // Advanced filtering
  const filteredItems = menuItems.filter(item => {
    if (!item.isActive || !item.inStock) return false;
    
    // Search filter
    if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !item.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Tag filter
    if (selectedTags.length > 0 && !selectedTags.some(tag => item.tags.includes(tag))) {
      return false;
    }
    
    // Dietary filter
    if (dietaryFilter !== 'all') {
      const selectedDietary = normalizeDietaryTag(dietaryFilter);
      const menuTags = (item.tags || []).map((tag) => normalizeDietaryTag(tag));
      if (!menuTags.includes(selectedDietary)) return false;
    }
    
    // Price range filter
    const effectivePrice = getEffectiveItemPrice(item);
    if (effectivePrice < priceRange[0] || effectivePrice > priceRange[1]) return false;
    
    // Spicy level filter
    if (spicyLevelFilter.length > 0 && !spicyLevelFilter.includes(item.spicyLevel || 0)) {
      return false;
    }
    
    return true;
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return getEffectiveItemPrice(a) - getEffectiveItemPrice(b);
      case 'price-high':
        return getEffectiveItemPrice(b) - getEffectiveItemPrice(a);
      case 'rating':
        // Sort by bestseller tag (demo)
        return (b.tags.includes('bestseller') ? 1 : 0) - (a.tags.includes('bestseller') ? 1 : 0);
      default:
        return 0;
    }
  });

  const offerScopedItems = (() => {
    const offer = selectedOffer;
    if (!offer) return sortedItems;

    const mode = (offer as any)?.applicableMode as ('all' | 'categories' | 'items' | undefined);
    if (!mode || mode === 'all') return sortedItems;

    if (mode === 'items') {
      const ids = Array.isArray((offer as any)?.selectedItemIds) ? (offer as any).selectedItemIds : [];
      const set = new Set(ids.map((v: any) => String(v)));
      if (set.size === 0) return sortedItems;
      return sortedItems.filter((item) => set.has(String(item.id)));
    }

    if (mode === 'categories') {
      const ids = Array.isArray((offer as any)?.selectedCategoryIds) ? (offer as any).selectedCategoryIds : [];
      const selected = new Set(ids.map((v: any) => String(v)));
      if (selected.size === 0) return sortedItems;

      const allowedMenuCategoryIds = new Set(
        (Array.isArray(categories) ? categories : [])
          .filter((cat) => selected.has(String(cat.id)) || (cat.foodCategoryId ? selected.has(String(cat.foodCategoryId)) : false))
          .map((cat) => String(cat.id))
      );

      if (allowedMenuCategoryIds.size === 0) return sortedItems;
      return sortedItems.filter((item) => allowedMenuCategoryIds.has(String(item.categoryId)));
    }

    return sortedItems;
  })();

  const getItemsForOffer = (offer: Coupon): MenuItem[] => {
    const mode = (offer as any)?.applicableMode as ('all' | 'categories' | 'items' | undefined);
    if (!mode || mode === 'all') return sortedItems;

    if (mode === 'items') {
      const ids = Array.isArray((offer as any)?.selectedItemIds) ? (offer as any).selectedItemIds : [];
      const set = new Set(ids.map((v: any) => String(v)));
      if (set.size === 0) return sortedItems;
      return sortedItems.filter((item) => set.has(String(item.id)));
    }

    if (mode === 'categories') {
      const ids = Array.isArray((offer as any)?.selectedCategoryIds) ? (offer as any).selectedCategoryIds : [];
      const selected = new Set(ids.map((v: any) => String(v)));
      if (selected.size === 0) return sortedItems;

      const allowedMenuCategoryIds = new Set(
        (Array.isArray(categories) ? categories : [])
          .filter(
            (cat) =>
              selected.has(String(cat.id)) ||
              (cat.foodCategoryId ? selected.has(String(cat.foodCategoryId)) : false)
          )
          .map((cat) => String(cat.id))
      );

      if (allowedMenuCategoryIds.size === 0) return sortedItems;
      return sortedItems.filter((item) => allowedMenuCategoryIds.has(String(item.categoryId)));
    }

    return sortedItems;
  };

  const offerCountsById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const offer of activeRestaurantOffers) {
      map[String(offer.id)] = getItemsForOffer(offer).length;
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRestaurantOffers, sortedItems, categories]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleSpicyLevel = (level: number) => {
    setSpicyLevelFilter(prev =>
      prev.includes(level) ? prev.filter(l => l !== level) : [...prev, level]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setDietaryFilter('all');
    setSortBy('relevance');
    setPriceRange([0, 1000]);
    setSpicyLevelFilter([]);
    setSelectedCategory(null);
  };

  const activeFilterCount = 
    (searchQuery ? 1 : 0) +
    selectedTags.length +
    (dietaryFilter !== 'all' ? 1 : 0) +
    (sortBy !== 'relevance' ? 1 : 0) +
    (priceRange[0] !== 0 || priceRange[1] !== 1000 ? 1 : 0) +
    spicyLevelFilter.length;

  const scrollToCategory = (categoryId: string) => {
    setSelectedCategory(categoryId);
    categoryRefs.current[categoryId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCheckout = () => {
    if (cart.size === 0 || isCheckingOut) return;

    if (isGuestUser) {
      sessionStorage.setItem('post_login_redirect', window.location.pathname || '/customer');
      if (selectedRestaurant?.id) {
        try {
          sessionStorage.setItem('post_login_selected_restaurant', JSON.stringify(selectedRestaurant));
        } catch {
          // ignore storage errors
        }
      }
      setShowAuthRequired(true);
      return;
    }
    
    // Close cart drawer and show payment modal
    setShowCart(false);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async (paymentMethod: string, paymentDetails?: any) => {
    if (!selectedRestaurant?.id || cart.size === 0) return;
    if (isGuestUser) {
      setShowPaymentModal(false);
      sessionStorage.setItem('post_login_redirect', window.location.pathname || '/customer');
      if (selectedRestaurant?.id) {
        try {
          sessionStorage.setItem('post_login_selected_restaurant', JSON.stringify(selectedRestaurant));
        } catch {
          // ignore storage errors
        }
      }
      setShowAuthRequired(true);
      return;
    }

    setShowPaymentModal(false);
    setIsCheckingOut(true);

    try {
      const parsedCustomerLatitude = Number(localStorage.getItem('customer_latitude') || '');
      const parsedCustomerLongitude = Number(localStorage.getItem('customer_longitude') || '');
      const payload = {
        restaurantId: selectedRestaurant.id,
        orderType: 'DELIVERY',
        deliveryMode: deliveryModeSelection,
        deliveryAddress: selectedLocation || 'Customer Address',
        couponCode: appliedCoupon?.code || undefined,
        couponId: appliedCoupon?.id || undefined,
        customerLatitude: Number.isFinite(parsedCustomerLatitude) ? parsedCustomerLatitude : undefined,
        customerLongitude: Number.isFinite(parsedCustomerLongitude) ? parsedCustomerLongitude : undefined,
        deliveryNotes: '',
        paymentMethod,
        subtotal,
        deliveryFee,
        tax,
        discount,
        total: grandTotal,
        items: Array.from(cart.values()).map((cartItem) => ({
          menuItemId: cartItem.item.id,
          quantity: cartItem.quantity,
          price: cartItem.selectedVariant?.price || cartItem.item.price,
          variantId: cartItem.selectedVariant?.id || undefined,
          addonIds: (cartItem.selectedAddOns || [])
            .filter((addon) => addon.source !== 'customization')
            .map((addon) => addon.id),
          customizations: (cartItem.selectedAddOns || [])
            .filter((addon) => addon.source === 'customization')
            .map((addon) => ({
              customizationOptionId: addon.customizationOptionId || addon.id,
              groupName: addon.groupName || addon.category,
              optionName: addon.name,
              price: addon.price,
            })),
          specialInstructions: cartItem.specialInstructions || undefined,
        })),
      };

      const response = await api.orders.create(payload);
      const createdOrder = (response as any)?.data;
      const createdOrderId = String(createdOrder?.id || '');
      const createdOrderNumber = String(createdOrder?.orderNumber || createdOrderId || '');

      setOrderDetails({
        orderNumber: createdOrderNumber,
        estimatedTime: selectedRestaurant?.deliveryTime || '30-40 mins',
        restaurantName: selectedRestaurant?.name || '',
        totalAmount: grandTotal,
        paymentMethod,
        orderId: createdOrderId || createdOrderNumber,
        paymentDetails,
      });

      setCart(new Map());
      setShowCart(false);
      setAppliedCoupon(null);
      setCouponCode('');
      setCouponError('');

      if (createdOrderId) {
        setActiveOrderId(createdOrderId);
        setShowBottomTracker(true);
      }

      setShowOrderSuccess(true);
    } catch (error: any) {
      alert(error?.message || 'Failed to place order. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleTrackOrder = (orderId?: string) => {
    console.log('handleTrackOrder called with orderId:', orderId);
    console.log('orderDetails:', orderDetails);
    const orderToTrack = orderId || orderDetails?.orderId;
    console.log('orderToTrack:', orderToTrack);
    if (orderToTrack) {
      setTrackingOrderId(orderToTrack);
      setShowOrderSuccess(false);
      setShowOrderTracking(true);
      console.log('Order tracking modal should open now');
    } else {
      console.log('No order to track');
    }
  };

  const handleViewOrderHistory = () => {
    setShowOrderHistory(true);
  };

  const handleRateOrder = (order: {
    id: string;
    restaurantId: string;
    restaurantName?: string;
    rating?: number;
  }) => {
    setReviewingOrder(order);
  };

  // Show location selection modal first (only required for signed-in users).
  // Guests can browse restaurants without selecting a location.
  if (!hasSelectedLocation && !isGuestUser) {
    return (
      <LocationSelectionModal
        isOpen={!hasSelectedLocation}
        userName={user.name}
        onLocationSelect={(location) => {
          setSelectedLocation(location);
          setHasSelectedLocation(true);
        }}
        onLogout={onLogout}
      />
    );
  }

  // Show order mode selection screen after location is selected
  if (!orderMode) {
    if (keyFromUrl && publicKeyBootLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-14 h-14 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading restaurant...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="customer-theme relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_10%_15%,#dbeafe_0%,transparent_32%),radial-gradient(circle_at_92%_20%,#ffedd5_0%,transparent_28%),linear-gradient(160deg,#f8fafc_0%,#eef2ff_45%,#fff7ed_100%)] px-4 py-8 sm:px-6 sm:py-10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-[8%] top-20 h-40 w-40 rounded-full bg-white/60 blur-2xl" />
          <div className="absolute bottom-10 right-[10%] h-48 w-48 rounded-full bg-blue-100/50 blur-3xl" />
        </div>

        <div className="relative mx-auto w-full max-w-6xl py-2 sm:py-4">
          <header className="mb-10 flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/90 px-3 py-1.5 text-sm font-semibold text-blue-700">
                <Sparkles className="h-4 w-4" />
                Start Your Food Journey
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">What would you like to do?</h1>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                Choose your preferred dining experience and continue with a flow designed for speed, clarity, and convenience.
              </p>
            </div>

            <div className="flex items-center gap-3 px-1 py-1">
              {isGuestUser ? (
                <button
                  type="button"
                  onClick={() => navigateToCustomerAuth('/customer-sign-in')}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
                  title="Sign In"
                >
                  <User className="h-5 w-5" />
                  <span>Sign In</span>
                </button>
              ) : (
                <>
                  {user.avatar && !modeAvatarError ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      onError={() => setModeAvatarError(true)}
                      className="h-12 w-12 rounded-xl object-cover ring-2 ring-blue-100/80"
                    />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-sm font-bold text-white">
                      {user.name?.trim()?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                  <div>
                    <p className="max-w-[180px] truncate text-base font-semibold text-slate-900">{user.name}</p>
                    <button
                      onClick={onLogout}
                      className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors hover:text-blue-600"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </header>

          <div className="grid overflow-hidden rounded-[28px] bg-white/40 backdrop-blur-sm md:grid-cols-2">
            <button
              onClick={() => setOrderMode('order-online')}
              className="group relative overflow-hidden p-7 text-left transition-colors duration-300 hover:bg-blue-50/45 lg:p-10"
            >
              <div className="absolute -right-16 -top-14 h-40 w-40 rounded-full bg-blue-100/70 transition-transform duration-500 group-hover:scale-125" />
              <div className="relative">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                  <ShoppingCart className="h-7 w-7" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900">Order Online</h2>
                <p className="mt-3 text-base leading-7 text-slate-600">
                  Browse menus, customize items, and get food delivered or prepared for pickup.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 transition-all group-hover:gap-3">
                  Start Ordering
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </button>

            <button
              onClick={() => setOrderMode('dine-out')}
              className="group relative overflow-hidden p-7 text-left transition-colors duration-300 hover:bg-amber-50/45 lg:p-10"
            >
              <div className="absolute -right-16 -top-14 h-40 w-40 rounded-full bg-amber-100/70 transition-transform duration-500 group-hover:scale-125" />
              <div className="relative">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 transition-colors group-hover:bg-amber-500 group-hover:text-white">
                  <UtensilsCrossed className="h-7 w-7" />
                </div>
                <h2 className="text-2xl font-semibold text-slate-900">Dine Out</h2>
                <p className="mt-3 text-base leading-7 text-slate-600">
                  Discover top restaurants, reserve tables, and unlock dine-in offers and curated experiences.
                </p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-600 transition-all group-hover:gap-3">
                  Explore Restaurants
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                <Star className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-slate-700">Top Rated</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-slate-700">Fast Delivery</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <Tag className="h-5 w-5 text-amber-600" />
              </div>
              <p className="text-sm font-medium text-slate-700">Great Offers</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100">
                <Store className="h-5 w-5 text-rose-600" />
              </div>
              <p className="text-sm font-medium text-slate-700">100+ Restaurants</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedRestaurant) {
    if (keyFromUrl && publicKeyBootLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-14 h-14 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading restaurant...</p>
          </div>
        </div>
      );
    }
    // For dine-out mode, show the DineOutView directly without selecting a specific restaurant
    if (orderMode === 'dine-out') {
      return (
        <DineOutView
          user={user}
          onLogout={onLogout}
          onBackToModeSelection={() => setOrderMode(null)}
          currentLocation={selectedLocation || 'Bangalore, Karnataka'}
          onLocationChange={(newLocation) => setSelectedLocation(newLocation)}
        />
      );
    }

    return (
      <>
        <RestaurantDiscovery
          user={user}
          onLogout={onLogout}
          onSelectRestaurant={(restaurant) => {
            const override = restaurantRatingOverrides[restaurant.id];
            setSelectedRestaurant(override ? { ...restaurant, ...override } : restaurant);
          }}
          restaurantRatingOverrides={restaurantRatingOverrides}
          orderMode={orderMode}
          onBackToModeSelection={() => setOrderMode(null)}
          currentLocation={selectedLocation || 'Bangalore, Karnataka'}
          onLocationChange={(newLocation) => setSelectedLocation(newLocation)}
          onViewOrderHistory={keyFromUrl ? undefined : handleViewOrderHistory}
          cartItemCount={cartItemCount}
          onOpenCart={() => {
            if (cartItemCount <= 0) return;
            if (selectedRestaurant) {
              setShowCart(true);
              return;
            }
            if (lastRestaurantRef.current) {
              setSelectedRestaurant(lastRestaurantRef.current);
              setOpenCartOnRestaurantLoad(true);
            }
          }}
        />

        {trackingOrderId && (
          <OrderTrackingModal
            isOpen={showOrderTracking}
            onClose={() => {
              setShowOrderTracking(false);
              setTrackingOrderId(null);
            }}
            orderId={trackingOrderId}
            onRateOrder={handleRateOrder}
          />
        )}

        <OrderHistoryModal
          isOpen={showOrderHistory}
          onClose={() => setShowOrderHistory(false)}
          onTrackOrder={(orderId) => handleTrackOrder(orderId)}
          onRateOrder={handleRateOrder}
        />

        <OrderReviewModal
          isOpen={Boolean(reviewingOrder)}
          order={reviewingOrder}
          onClose={() => setReviewingOrder(null)}
          onSubmitted={({ orderId, restaurantId, rating }) => {
            const currentRestaurant = selectedRestaurant as Restaurant | null;
            const currentRestaurantId = currentRestaurant ? String(currentRestaurant.id) : null;
            const baseRestaurant: Restaurant | null =
              currentRestaurantId === restaurantId
                ? effectiveSelectedRestaurant || currentRestaurant
                : null;
            const currentTotalRatings = Number(baseRestaurant?.totalRatings || restaurantRatingOverrides[restaurantId]?.totalRatings || 0);
            const currentRating = Number(baseRestaurant?.rating || restaurantRatingOverrides[restaurantId]?.rating || 0);
            const previousRating = reviewingOrder?.restaurantId === restaurantId ? Number(reviewingOrder?.rating || 0) : 0;
            const nextTotalRatings = previousRating > 0 ? Math.max(currentTotalRatings, 1) : currentTotalRatings + 1;
            const nextRating =
              previousRating > 0
                ? Number((((currentRating * Math.max(currentTotalRatings, 1)) - previousRating + rating) / Math.max(currentTotalRatings, 1)).toFixed(1))
                : Number((((currentRating * currentTotalRatings) + rating) / Math.max(nextTotalRatings, 1)).toFixed(1));

            setRestaurantRatingOverrides((prev) => ({
              ...prev,
              [restaurantId]: {
                rating: nextRating,
                totalRatings: nextTotalRatings,
              },
            }));

            if (currentRestaurantId === restaurantId) {
              setSelectedRestaurant((prev) =>
                prev
                  ? {
                      ...prev,
                      rating: nextRating,
                      totalRatings: nextTotalRatings,
                    }
                  : prev
              );
            }

            if (trackingOrderId === orderId) {
              setTrackingOrderId(orderId);
            }
            setReviewingOrder(null);
          }}
        />

        {showBottomTracker && activeOrderId && (
          <BottomOrderTrackingBar
            orderId={activeOrderId}
            onOpenTracking={(orderId) => {
              setTrackingOrderId(orderId);
              setShowOrderTracking(true);
            }}
            onDismiss={() => {
              setShowBottomTracker(false);
              setActiveOrderId(null);
            }}
          />
        )}
      </>
    );
  }

  // For order-online mode, show the restaurant menu
  return (
    <div className="customer-theme min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {/* Header */}
      <div 
        className="relative z-40 border-b border-slate-200 bg-white text-slate-900 dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-800 dark:text-white"
      >
        <div className="absolute inset-0 hidden dark:block bg-gradient-to-r from-black/25 to-black/0 backdrop-blur-sm" />
        <div className="relative py-3 sm:py-4">
          <div className="mx-auto w-full max-w-screen-2xl px-3 sm:px-4">
          <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
            {!keyFromUrl ? (
              <button
                type="button"
                onClick={() => navigateToPath('/')}
                className="flex items-center gap-2 text-slate-900 transition-colors hover:text-slate-700 dark:text-white dark:hover:text-white/95"
                title="Go to Landing Page"
              >
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FC8019] to-[#E84545] flex items-center justify-center shadow-lg shadow-orange-500/20">
                  <UtensilsCrossed className="w-5 h-5 text-white" />
                </span>
                <span className="text-lg font-extrabold tracking-tight">FoodHub</span>
              </button>
            ) : (
              <div aria-hidden="true" className="w-0" />
            )}
            <div className="flex items-center gap-1.5 sm:gap-3">
              {!keyFromUrl ? (
                <button
                  onClick={handleViewOrderHistory}
                  className="rounded-lg bg-slate-900/5 p-2 transition-colors hover:bg-slate-900/10 dark:bg-white/20 dark:hover:bg-white/30"
                  title="Order History"
                >
                  <Receipt className="h-5 w-5 text-slate-700 dark:text-white sm:h-6 sm:w-6" />
                </button>
              ) : null}
              {canOrderInApp ? (
                <button
                  onClick={() => setShowCart(true)}
                  className="relative rounded-lg bg-slate-900/5 p-2 transition-colors hover:bg-slate-900/10 dark:bg-white/20 dark:hover:bg-white/30"
                >
                  <ShoppingCart className="h-5 w-5 text-slate-700 dark:text-white sm:h-6 sm:w-6" />
                  {cart.size > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FB923C] text-xs font-bold text-white">
                      {cart.size}
                    </span>
                  )}
                </button>
              ) : null}
              <ProfileDropdown
                user={user}
                onLogout={onLogout}
                onSignIn={() => navigateToCustomerAuth('/customer-sign-in')}
                onOpenSettings={(section) => setShowSettings(section || 'account')}
                onOpenOrderHistory={keyFromUrl ? undefined : handleViewOrderHistory}
                variant="compact"
              />
            </div>
          </div>

          {/* Restaurant Info */}
          <div className="mb-4 rounded-3xl border border-white/30 bg-white/90 p-4 shadow-[0_25px_60px_rgba(0,0,0,0.28)] backdrop-blur-md sm:p-6">
            <div className="grid grid-cols-[auto,1fr] items-start gap-x-3 gap-y-2 sm:gap-x-6 sm:gap-y-2">
              <img
                src={effectiveSelectedRestaurant?.logo}
                alt={effectiveSelectedRestaurant?.name}
                className="row-span-1 h-14 w-14 self-start rounded-2xl object-cover shadow-2xl ring-1 ring-black/10 sm:row-span-2 sm:h-44 sm:w-44 sm:rounded-3xl"
              />
              <div className="min-w-0 text-left">
                <h2 className="text-xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl">
                  {effectiveSelectedRestaurant?.name}
                </h2>

                <div className="mt-2 flex flex-wrap items-center justify-start gap-2 text-slate-600 sm:mt-3 sm:gap-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-extrabold leading-none text-white shadow-sm sm:gap-2 sm:px-4 sm:py-2 sm:text-lg sm:leading-none">
                    {((effectiveSelectedRestaurant?.totalRatings || 0) > 0)
                      ? Number(effectiveSelectedRestaurant?.rating || 0).toFixed(1)
                      : '0.0'}
                    <Star className="h-3 w-3 fill-white text-white sm:h-5 sm:w-5" />
                  </span>
                  {(effectiveSelectedRestaurant?.totalRatings || 0) > 0 ? (
                    <span className="text-[11px] font-medium text-slate-600 sm:text-lg">
                      ({formatCompactCount(Number(effectiveSelectedRestaurant?.totalRatings || 0))} ratings)
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="col-span-2 text-center sm:col-span-1 sm:col-start-2 sm:text-left">
                <p className="mt-2 text-[12px] leading-snug text-slate-600 sm:text-sm">
                  {isHeaderDescriptionExpanded ? (
                    <>
                      <span className="whitespace-pre-line">{headerDescriptionText}</span>
                      {headerDescriptionText ? (
                        <button
                          type="button"
                          onClick={() => setIsHeaderDescriptionExpanded(false)}
                          className="ml-1 inline text-[11px] font-extrabold text-[#2563EB] hover:text-[#1D4ED8]"
                        >
                          {t('common.less', 'less')}
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <span className="relative block">
                      <span ref={headerDescriptionRef} className="line-clamp-2">
                        {headerDescriptionText}
                      </span>
                      {isHeaderDescriptionOverflowing && (
                        <button
                          type="button"
                          onClick={() => setIsHeaderDescriptionExpanded(true)}
                          className="absolute bottom-[2px] right-0 rounded-sm bg-white/95 px-1 text-[11px] font-extrabold leading-none text-[#2563EB] shadow-sm hover:text-[#1D4ED8]"
                        >
                          …{t('common.more', 'more')}
                        </button>
                      )}
                    </span>
                  )}
                </p>

                {headerAddressText ? (
                  <div className="mt-2 flex items-start justify-center gap-2 text-[12px] text-slate-600 sm:justify-start sm:text-sm">
                    <MapPin className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                    <span className="line-clamp-1">{headerAddressText}</span>
                  </div>
                ) : null}

                <div className="mt-2 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-slate-600 sm:mt-3 sm:justify-start sm:text-sm">
                  <span className="inline-flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" />
                    <span>{effectiveSelectedRestaurant?.deliveryTime}</span>
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {/* <MapPin className="h-3.5 w-3.5" /> */}
                    <span>
                      {deliveryFee === 0
                        ? t('delivery.free', 'Free delivery')
                        : `${formatMoney(baseDeliveryFee)} delivery fee`}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex items-stretch gap-1 sm:gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 transform text-gray-400 sm:left-3 sm:h-5 sm:w-5" />
              <input
                type="text"
                placeholder="Search for dishes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-white/30 bg-white/95 py-1 pl-7 pr-2 text-[12px] text-gray-900 placeholder-gray-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-white sm:py-3 sm:pl-10 sm:pr-4 sm:text-base"
              />
            </div>
            <button
              onClick={() => setShowFilters(true)}
              aria-label="Open filters"
              className={`relative flex w-8 flex-shrink-0 items-center justify-center rounded-lg px-0 py-0.5 shadow-sm transition-colors sm:w-auto sm:gap-2 sm:px-4 sm:py-3 ${
                activeFilterCount > 0 
                  ? 'bg-[#FB923C] text-white hover:bg-[#F97316]' 
                  : 'bg-white/95 text-gray-700 border border-white/30 hover:bg-white'
              }`}
            >
              <Filter className="h-3 w-3 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline font-medium">Filters</span>
              {activeFilterCount > 0 && (
                <span className="absolute -right-1 -top-1 rounded-full bg-white px-1.5 py-0.5 text-xs font-bold text-[#FB923C] sm:static sm:ml-1 sm:px-2">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
          </div>
      </div>

      {/* Filter Modal */}
      {showFilters && (
        <MenuFilterModal
          dietaryFilter={dietaryFilter}
          sortBy={sortBy}
          priceRange={priceRange}
          spicyLevelFilter={spicyLevelFilter}
          onClose={() => setShowFilters(false)}
          onApply={(filters) => {
            setDietaryFilter(filters.dietaryFilter);
            setSortBy(filters.sortBy);
            setPriceRange(filters.priceRange);
            setSpicyLevelFilter(filters.spicyLevelFilter);
          }}
        />
      )}

      {/* Restaurant Tabs with All Sections */}
      <RestaurantTabs
        restaurant={effectiveSelectedRestaurant || selectedRestaurant}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        offers={activeRestaurantOffers}
        selectedOfferId={selectedOffer?.id || null}
        onOfferSelect={(offer) => {
          setSelectedCategory(null);
          setSelectedOffer((prev) => (prev?.id === offer.id ? null : offer));
          setActiveTab('order-online');
        }}
      >
        {/* Order Online Tab Content */}
        <div>
          {/* Category Navigation */}
          <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
            <div className="px-3 sm:px-4">
              <div className="flex overflow-x-auto gap-1 py-1.5 no-scrollbar sm:gap-2 sm:py-3">
                <button
                  onClick={() => {
                    setSelectedOffer(null);
                    setSelectedCategory(null);
                  }}
                  className={`whitespace-nowrap rounded px-2.5 py-1 text-[11px] font-medium leading-none transition-all sm:rounded-lg sm:px-4 sm:py-2 sm:text-sm sm:leading-normal ${
                    !selectedOffer && selectedCategory === null
                      ? 'bg-[#60A5FA] text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Items ({sortedItems.length})
                </button>

                {activeRestaurantOffers.slice(0, 20).map((offer) => {
                  const active = selectedOffer?.id === offer.id;
                  const count = offerCountsById[String(offer.id)] ?? getItemsForOffer(offer).length;
                  return (
                    <button
                      key={offer.id}
                      type="button"
                      onClick={() => {
                        setSelectedCategory(null);
                        setSelectedOffer((prev) => (prev?.id === offer.id ? null : offer));
                      }}
                      className={`whitespace-nowrap rounded px-2.5 py-1 text-[11px] font-bold leading-none transition-all sm:rounded-lg sm:px-4 sm:py-2 sm:text-sm sm:leading-normal ${
                        active
                          ? 'bg-[#FB923C] text-white shadow-sm'
                          : 'bg-[#FB923C]/10 text-[#FB923C] hover:bg-[#FB923C]/20'
                      }`}
                      title={String(offer.description || '').trim()}
                    >
                      {String(offer.code || 'OFFER').trim()} {offerChipLabel(offer)} ({count})
                    </button>
                  );
                })}

                {categories.map(category => {
                  const baseItems = selectedOffer ? sortedItems : offerScopedItems;
                  const count = baseItems.filter(item => item.categoryId === category.id).length;
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        if (count === 0) return;
                        setSelectedOffer(null);
                        scrollToCategory(category.id);
                      }}
                      disabled={count === 0}
                      className={`whitespace-nowrap rounded px-2.5 py-1 text-[11px] font-medium leading-none transition-all sm:rounded-lg sm:px-4 sm:py-2 sm:text-sm sm:leading-normal ${
                        !selectedOffer && selectedCategory === category.id
                          ? 'bg-[#60A5FA] text-white shadow-sm'
                          : count === 0
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {category.name} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Results Summary */}
          {activeFilterCount > 0 && (
            <div className="px-3 py-3 sm:px-4">
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-900">
                  Showing {offerScopedItems.length} items with {activeFilterCount} active filter{activeFilterCount > 1 ? 's' : ''}
                </p>
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-[#60A5FA] hover:text-[#3B82F6] font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="px-3 py-5 pb-24 sm:px-4 sm:py-6">
            {selectedCategory === null ? (
              selectedOffer ? (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-900 sm:text-xl">
                      {String(selectedOffer.code || 'Offer').trim()} {offerChipLabel(selectedOffer)}
                    </h3>
                    <span className="text-[11px] text-gray-600 sm:text-sm">{offerScopedItems.length} items</span>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                    {offerScopedItems.map(item => (
                      <MenuItem key={item.id} item={item} />
                    ))}
                  </div>
                </div>
              ) : (
                // Show all items grouped by category
                categories.map(category => {
                  const categoryItems = offerScopedItems.filter(item => item.categoryId === category.id);
                  if (categoryItems.length === 0) return null;

                  return (
                    <div
                      key={category.id}
                      ref={el => categoryRefs.current[category.id] = el}
                      className="mb-8 scroll-mt-[96px]"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-semibold text-gray-900 sm:text-xl">{category.name}</h3>
                        <span className="text-[11px] text-gray-600 sm:text-sm">{categoryItems.length} items</span>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                        {categoryItems.map(item => (
                          <MenuItem key={item.id} item={item} />
                        ))}
                      </div>
                    </div>
                  );
                })
              )
            ) : (
              // Show only selected category
              <div
                ref={el => categoryRefs.current[selectedCategory] = el}
                className="scroll-mt-[96px]"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900 sm:text-xl">
                    {categories.find(c => c.id === selectedCategory)?.name}
                  </h3>
                  <span className="text-[11px] text-gray-600 sm:text-sm">
                    {offerScopedItems.filter(item => item.categoryId === selectedCategory).length} items
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {offerScopedItems
                    .filter(item => item.categoryId === selectedCategory)
                    .map(item => (
                      <MenuItem key={item.id} item={item} />
                    ))}
                </div>
              </div>
            )}

            {menuLoading && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg mb-2">Loading menu...</p>
              </div>
            )}

            {!menuLoading && offerScopedItems.length === 0 && (
              <div className="text-center py-12">
                <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">No items found</p>
                <p className="text-gray-400 text-sm mb-4">Try adjusting your filters</p>
                <button
                  onClick={clearAllFilters}
                  className="bg-[#60A5FA] text-white px-6 py-2 rounded-lg hover:bg-[#3B82F6] transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </RestaurantTabs>

      {/* Cart Button (Mobile) */}
      {cart.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-40 md:hidden">
          <button
            onClick={() => setShowCart(true)}
            className="w-full bg-[#60A5FA] text-white py-3 rounded-lg flex items-center justify-between font-medium hover:bg-[#3B82F6] transition-colors"
          >
            <span>{cart.size} items</span>
            <span>View Cart â€¢ {formatMoney(grandTotal)}</span>
          </button>
        </div>
      )}

      {/* Customize Item Modal */}
      {customizingItem && (
        <CustomizeItemModal
          item={customizingItem}
          onClose={() => setCustomizingItem(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        totalAmount={grandTotal}
        onPaymentComplete={handlePaymentComplete}
        cardStorageKey={CARD_STORAGE_KEY}
        walletStorageKey={WALLET_STORAGE_KEY}
      />

      <AuthRequiredModal
        isOpen={showAuthRequired}
        message={authRequiredMessage}
        onClose={() => setShowAuthRequired(false)}
        placement={showCart ? 'drawer' : 'center'}
        onLogin={() => {
          setShowAuthRequired(false);
          navigateToCustomerAuth('/customer-sign-in');
        }}
        onSignup={() => {
          setShowAuthRequired(false);
          navigateToCustomerAuth('/customer-sign-up');
        }}
      />

      {/* Order Success Modal */}
      {orderDetails && (
        <OrderSuccessModal
          isOpen={showOrderSuccess}
          onClose={() => {
            setShowOrderSuccess(false);
            setOrderDetails(null);
          }}
          orderNumber={orderDetails.orderNumber}
          estimatedTime={orderDetails.estimatedTime}
          restaurantName={orderDetails.restaurantName}
          totalAmount={orderDetails.totalAmount}
          paymentMethod={orderDetails.paymentMethod}
          onTrackOrder={() => handleTrackOrder()}
        />
      )}

      {/* Order Tracking Modal */}
      {trackingOrderId && (
        <OrderTrackingModal
          isOpen={showOrderTracking}
          onClose={() => {
            setShowOrderTracking(false);
            setTrackingOrderId(null);
          }}
          orderId={trackingOrderId}
          onRateOrder={handleRateOrder}
        />
      )}

      {/* Order History Modal */}
      <OrderHistoryModal
        isOpen={showOrderHistory}
        onClose={() => setShowOrderHistory(false)}
        onTrackOrder={(orderId) => handleTrackOrder(orderId)}
        onRateOrder={handleRateOrder}
      />

      <OrderReviewModal
        isOpen={Boolean(reviewingOrder)}
        order={reviewingOrder}
        onClose={() => setReviewingOrder(null)}
        onSubmitted={({ orderId, restaurantId, rating }) => {
          const baseRestaurant =
            selectedRestaurant?.id === restaurantId
              ? (effectiveSelectedRestaurant || selectedRestaurant)
              : null;
          const currentTotalRatings = Number(baseRestaurant?.totalRatings || restaurantRatingOverrides[restaurantId]?.totalRatings || 0);
          const currentRating = Number(baseRestaurant?.rating || restaurantRatingOverrides[restaurantId]?.rating || 0);
          const previousRating = reviewingOrder?.restaurantId === restaurantId ? Number(reviewingOrder?.rating || 0) : 0;
          const nextTotalRatings = previousRating > 0 ? Math.max(currentTotalRatings, 1) : currentTotalRatings + 1;
          const nextRating =
            previousRating > 0
              ? Number((((currentRating * Math.max(currentTotalRatings, 1)) - previousRating + rating) / Math.max(currentTotalRatings, 1)).toFixed(1))
              : Number((((currentRating * currentTotalRatings) + rating) / Math.max(nextTotalRatings, 1)).toFixed(1));

          setRestaurantRatingOverrides((prev) => ({
            ...prev,
            [restaurantId]: {
              rating: nextRating,
              totalRatings: nextTotalRatings,
            },
          }));

          if (selectedRestaurant?.id === restaurantId) {
            setSelectedRestaurant((prev) =>
              prev
                ? {
                    ...prev,
                    rating: nextRating,
                    totalRatings: nextTotalRatings,
                  }
                : prev
            );
          }

          if (trackingOrderId === orderId) {
            setTrackingOrderId(orderId);
          }
          setReviewingOrder(null);
        }}
      />

      {/* Customer Settings Modal */}
      {showSettings && (
        <CustomerSettings
          user={user}
          onClose={() => setShowSettings(null)}
          onLogout={onLogout}
          initialSection={showSettings as any}
        />
      )}

      {/* Bottom Order Tracking Bar */}
      {showBottomTracker && activeOrderId && (
        <BottomOrderTrackingBar
          orderId={activeOrderId}
          onOpenTracking={(orderId) => {
            setTrackingOrderId(orderId);
            setShowOrderTracking(true);
          }}
          onDismiss={() => {
            setShowBottomTracker(false);
            setActiveOrderId(null);
          }}
        />
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCart(false)}>
          <div 
            className="absolute right-0 top-0 bottom-0 w-full md:w-96 bg-white shadow-xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-gray-900">{t('cart.title', 'Your Cart')}</h2>
                <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {cartItems.length === 0 ? (
                <p className="text-gray-600 text-center py-8">{t('cart.empty', 'Your cart is empty')}</p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cartItems.map((cartItem) => {
                      const basePrice = cartItem.selectedVariant?.price || cartItem.item.price;
                      const addOnsPrice = cartItem.selectedAddOns.reduce((sum, addOn) => sum + addOn.price, 0);
                      const itemTotal = (basePrice + addOnsPrice) * cartItem.quantity;

                      return (
                        <div key={cartItem.cartKey} className="pb-4 border-b border-gray-200">
                          <div className="flex items-start gap-3 mb-2">
                            <div className="flex-1">
                              <h4 className="text-gray-900 mb-1">{cartItem.item.name}</h4>
                              {cartItem.item.servingSize && (
                                <p className="text-gray-600 text-xs">• {formatServingSizeLabel(cartItem.item.servingSize) || cartItem.item.servingSize}</p>
                              )}
                              {cartItem.selectedVariant && (
                                <p className="text-gray-600 text-xs">â€¢ {cartItem.selectedVariant.name}</p>
                              )}
                              {cartItem.selectedAddOns.length > 0 && (
                                <div className="text-gray-600 text-xs">
                                  {cartItem.selectedAddOns.map(addOn => (
                                    <p key={addOn.id}>
                                      â€¢ {addOn.name} {addOn.price > 0 && `(+${formatMoney(addOn.price)})`}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {cartItem.specialInstructions && (
                                <p className="text-gray-500 text-xs italic mt-1">"{cartItem.specialInstructions}"</p>
                              )}
                            </div>
                            <span className="text-gray-900">{formatMoney(itemTotal)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => decrementCartItem(cartItem.cartKey)}
                              className="w-7 h-7 bg-gray-200 text-gray-700 rounded flex items-center justify-center hover:bg-gray-300"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="text-gray-900 w-8 text-center">{cartItem.quantity}</span>
                            <button
                              onClick={() => incrementCartItem(cartItem.cartKey)}
                              className="w-7 h-7 bg-gray-200 text-gray-700 rounded flex items-center justify-center hover:bg-gray-300"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Coupon Section */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <div className="flex gap-2 mb-2">
                      <div className="flex-1 relative">
                        <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder={t('cart.coupon.placeholder', 'Enter coupon code')}
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#60A5FA]"
                        />
                      </div>
                      <button
                        onClick={applyCoupon}
                        className="bg-[#60A5FA] text-white px-4 py-2 rounded-lg hover:bg-[#3B82F6] transition-colors"
                      >
                        {t('cart.coupon.apply', 'Apply')}
                      </button>
                    </div>
                    {couponError && (
                      <div className="mt-2 text-red-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {couponError}
                      </div>
                    )}
                    {appliedCoupon && (
                      <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-700 text-sm">
                          <Tag className="w-4 h-4" />
                          <span>
                            {appliedCoupon.code} {t('cart.coupon.applied', 'applied')} â€¢ {formatMoney(-discount)}
                          </span>
                        </div>
                        <button onClick={removeCoupon} className="text-green-700 hover:text-green-800">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Bill Summary */}
                  <div className="space-y-2 mb-6 pb-6 border-b border-gray-200">
                    <div className="flex justify-between text-gray-700">
                      <span>{t('cart.summary.itemTotal', 'Item Total')}</span>
                      <span>{formatMoney(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>{t('cart.summary.deliveryFee', 'Delivery Fee')}</span>
                      {deliveryFee === 0 ? (
                        <span className="text-[#4ADE80]">{t('common.free', 'FREE')}</span>
                      ) : (
                        <span>{formatMoney(deliveryFee)}</span>
                      )}
                    </div>
                    <div className="flex justify-between text-gray-700">
                      <span>{t('cart.summary.packaging', 'Packaging Charges')}</span>
                      <span>{formatMoney(packagingCharges)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-[#4ADE80]">
                        <span>{t('cart.summary.discount', 'Discount')}</span>
                        <span>{formatMoney(-discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-gray-700">
                      <span>{t('cart.summary.taxes', 'Taxes & Charges')}</span>
                      <span>{formatMoney(tax)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="text-gray-900">{t('cart.summary.toPay', 'To Pay')}</span>
                      <span className="text-gray-900">{formatMoney(grandTotal)}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckout}
                    disabled={isCheckingOut}
                    className="w-full bg-[#60A5FA] text-white py-3 rounded-lg hover:bg-[#3B82F6] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCheckingOut
                      ? t('cart.checkout.processing', 'Processing Order...')
                      : t('cart.checkout.cta', 'Proceed to Checkout â€¢ {amount}', { amount: formatMoney(grandTotal) })}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // MenuItem Component
  function MenuItem({ item }: { item: MenuItem }) {
    const totalInCart = getItemTotalInCart(item.id);

    // Keep any item-level price overrides (discountPrice) for pricing display only.
    const getPricingMeta = (): { originalPrice: number; finalPrice: number } | null => {
      const originalPrice = Number(item.price || 0);
      if (!Number.isFinite(originalPrice) || originalPrice <= 0) return null;
      const rawDiscountPrice =
        (item as any)?.discountPrice ?? (item as any)?.discount_price ?? (item as any)?.discountedPrice;
      if (rawDiscountPrice === null || rawDiscountPrice === undefined || rawDiscountPrice === '') return null;
      const parsedDiscountPrice = Number(rawDiscountPrice);
      if (!Number.isFinite(parsedDiscountPrice) || parsedDiscountPrice <= 0) return null;
      if (parsedDiscountPrice >= originalPrice) return null;
      return { originalPrice, finalPrice: parsedDiscountPrice };
    };

    // Offer badge comes ONLY from restaurant_offers (active + within dates).
    const getBestOfferForItem = (): { badge: string; tone: 'orange' | 'green' | 'red' } | null => {
      const candidates = selectedOffer ? [selectedOffer] : activeRestaurantOffers;
      if (!candidates.length) return null;

      const itemId = String(item.id);
      const menuCategoryId = String(item.categoryId);
      const menuCategory = categories.find((c) => String(c.id) === menuCategoryId) || null;

      const isOfferApplicable = (offer: Coupon): boolean => {
        const mode = (offer as any)?.applicableMode as ('all' | 'categories' | 'items' | undefined);
        if (!mode || mode === 'all') return true;
        if (mode === 'items') {
          const ids = Array.isArray((offer as any)?.selectedItemIds) ? (offer as any).selectedItemIds : [];
          return ids.map((v: any) => String(v)).includes(itemId);
        }
        if (mode === 'categories') {
          const ids = Array.isArray((offer as any)?.selectedCategoryIds) ? (offer as any).selectedCategoryIds : [];
          const set = new Set(ids.map((v: any) => String(v)));
          if (set.size === 0) return true;
          if (set.has(menuCategoryId)) return true;
          if (menuCategory?.foodCategoryId && set.has(String(menuCategory.foodCategoryId))) return true;
          return false;
        }
        return true;
      };

      const price = getEffectiveItemPrice(item);
      const estimateSavings = (offer: Coupon): number => {
        const value = Number(offer.discountValue || 0);
        if (!Number.isFinite(value) || value <= 0) return 0;
        if (offer.discountType === 'percentage') {
          let off = (price * value) / 100;
          const cap = Number((offer as any).maxDiscount ?? offer.maxDiscount ?? 0);
          if (Number.isFinite(cap) && cap > 0) off = Math.min(off, cap);
          return off;
        }
        let off = value;
        const cap = Number((offer as any).maxDiscount ?? offer.maxDiscount ?? 0);
        if (Number.isFinite(cap) && cap > 0) off = Math.min(off, cap);
        return off;
      };

      let best: Coupon | null = null;
      let bestSaving = 0;
      for (const offer of candidates) {
        if (!isOfferApplicable(offer)) continue;
        const saving = estimateSavings(offer);
        if (saving > bestSaving) {
          bestSaving = saving;
          best = offer;
        }
      }

      if (!best || bestSaving <= 0) return null;
      const badge = offerChipLabel(best);
      const value = Number(best.discountValue || 0);
      const tone: 'orange' | 'green' | 'red' =
        best.discountType === 'flat' ? 'red' : value >= 20 ? 'orange' : 'green';
      return { badge, tone };
    };

    const pricingMeta = getPricingMeta();
    const resolvedOfferMeta = getBestOfferForItem();
    const getDietIndicator = () => {
      const normalized = new Set((item.tags || []).map(normalizeDietaryTag).filter(Boolean));
      if (normalized.has('veg')) {
        return (
          <div className="w-5 h-5 border-2 border-[#4ADE80] rounded flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-[#4ADE80]" />
          </div>
        );
      }
      if (normalized.has('non-veg')) {
        return (
          <div className="w-5 h-5 border-2 border-red-500 rounded flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
          </div>
        );
      }
      if (normalized.has('jain')) {
        return (
          <div className="w-5 h-5 border-2 border-purple-500 rounded flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-purple-500" />
          </div>
        );
      }
      return null;
    };

    const hasCustomizations =
      (item.variants && item.variants.length > 0) ||
      (item.addOns && item.addOns.length > 0) ||
      (item.customizationGroups && item.customizationGroups.length > 0);

    const coerceNumber = (value: any) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const match = value.match(/-?\d+(?:\.\d+)?/);
        return match ? Number(match[0]) : Number(value);
      }
      return Number(value);
    };

    const ratingValueRaw =
      (item as any)?.rating ?? (item as any)?.avgRating ?? (item as any)?.averageRating ?? (item as any)?.average_rating;
    const ratingCountRaw =
      (item as any)?.ratingCount ?? (item as any)?.reviewCount ?? (item as any)?.totalReviews ?? (item as any)?.total_reviews;

    const restaurantRatingRaw =
      (selectedRestaurant as any)?.rating ?? (selectedRestaurant as any)?.avgRating ?? (selectedRestaurant as any)?.average_rating;

    const baseRating = coerceNumber(ratingValueRaw);
    const fallbackRating = coerceNumber(restaurantRatingRaw);
    const ratingValue = Number.isFinite(baseRating) && baseRating > 0 ? baseRating : fallbackRating;
    const ratingCount = coerceNumber(ratingCountRaw);
    const showRating = Number.isFinite(ratingValue) && ratingValue > 0;

    const effectiveFinalPrice =
      pricingMeta?.finalPrice !== undefined &&
      pricingMeta?.originalPrice !== undefined &&
      Number.isFinite(pricingMeta.finalPrice) &&
      pricingMeta.finalPrice > 0 &&
      pricingMeta.finalPrice < pricingMeta.originalPrice
        ? pricingMeta.finalPrice
        : null;

    const descriptionText = String(item.description || '').trim();
    const hasDescription = Boolean(descriptionText);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const mobileDescRef = useRef<HTMLSpanElement | null>(null);
    const desktopDescRef = useRef<HTMLSpanElement | null>(null);
    const [isMobileDescriptionOverflowing, setIsMobileDescriptionOverflowing] = useState(false);
    const [isDesktopDescriptionOverflowing, setIsDesktopDescriptionOverflowing] = useState(false);
    const servingSizeLabel = formatServingSizeLabel(item.servingSize);

    const shouldShowMobileMore = hasDescription && !isDescriptionExpanded && isMobileDescriptionOverflowing;
    const shouldShowDesktopMore = hasDescription && !isDescriptionExpanded && isDesktopDescriptionOverflowing;

    useEffect(() => {
      if (!hasDescription || isDescriptionExpanded) return;

      const check = (el: HTMLSpanElement | null, setter: (value: boolean) => void) => {
        if (!el) return;
        if (el.clientHeight <= 0) return;
        setter(el.scrollHeight > el.clientHeight + 1);
      };

      const runAll = () => {
        check(mobileDescRef.current, setIsMobileDescriptionOverflowing);
        check(desktopDescRef.current, setIsDesktopDescriptionOverflowing);
      };

      runAll();

      let mobileObserver: ResizeObserver | null = null;
      let desktopObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        mobileObserver = new ResizeObserver(runAll);
        desktopObserver = new ResizeObserver(runAll);
        if (mobileDescRef.current) mobileObserver.observe(mobileDescRef.current);
        if (desktopDescRef.current) desktopObserver.observe(desktopDescRef.current);
      }

      window.addEventListener('resize', runAll);
      return () => {
        window.removeEventListener('resize', runAll);
        mobileObserver?.disconnect();
        desktopObserver?.disconnect();
      };
    }, [descriptionText, hasDescription, isDescriptionExpanded]);

    return (
      <div className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-gradient-to-br dark:from-slate-900/70 dark:to-slate-800/30 dark:shadow-[0_18px_50px_rgba(0,0,0,0.35)] dark:backdrop-blur-sm dark:hover:border-white/20">
        {/* Mobile: Zomato-style side-by-side */}
        <div className="sm:hidden p-3 min-[360px]:p-4">
          <div className="flex gap-2.5 min-[360px]:gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {getDietIndicator()}
	                  {item.tags.includes('bestseller') && (
	                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[8px] min-[360px]:px-2.5 min-[360px]:py-1 min-[360px]:text-[9px] font-semibold uppercase tracking-wide text-gray-800 shadow-sm dark:bg-white/15 dark:text-white/90">
	                      {t('menu.badge.bestseller', 'Bestseller')}
	                    </span>
	                  )}
                </div>
	                {showRating && (
	                  <div className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-1.5 py-0.5 text-[10px] min-[360px]:px-2 min-[360px]:py-1 min-[360px]:text-[11px] font-extrabold text-white">
	                    <Star className="h-3.5 w-3.5 fill-white text-white min-[360px]:h-4 min-[360px]:w-4" />
	                    <span>{ratingValue.toFixed(1)}</span>
	                  </div>
	                )}
	              </div>

	              <h4 className="mt-2 line-clamp-1 text-[14px] min-[360px]:text-[15px] font-extrabold leading-tight text-gray-900 dark:text-white">
	                {item.name}
	              </h4>

	              {hasDescription && (
	                <p className="mt-2 text-[10px] min-[360px]:text-[11px] leading-[1.35] text-gray-600 dark:text-white/70">
	                  {isDescriptionExpanded ? (
	                    <>
	                      <span className="whitespace-pre-line">{descriptionText}</span>
	                      <button
	                        type="button"
	                        onClick={() => setIsDescriptionExpanded(false)}
	                        className="ml-1 inline text-[10px] font-extrabold text-[#2563EB] hover:text-[#1D4ED8] dark:text-amber-300 dark:hover:text-amber-200"
	                      >
	                        {t('common.less', 'less')}
	                      </button>
	                    </>
                  ) : (
                    <span className="relative block">
                      <span ref={mobileDescRef} className="line-clamp-2">
                        {descriptionText}
                      </span>
	                      {shouldShowMobileMore && (
	                        <button
	                          type="button"
	                          onClick={() => setIsDescriptionExpanded(true)}
	                          className="absolute bottom-[2px] right-0 rounded-sm bg-white/95 px-1 text-[10px] font-extrabold leading-none text-[#2563EB] shadow-sm hover:text-[#1D4ED8] dark:bg-slate-900/70 dark:text-amber-300 dark:hover:text-amber-200 dark:shadow-none"
	                        >
	                          …{t('common.more', 'more')}
	                        </button>
	                      )}
                    </span>
                  )}
                </p>
              )}
            </div>

            <div className="shrink-0 w-24 min-[360px]:w-32">
              <div className="relative h-20 w-24 min-[360px]:h-24 min-[360px]:w-32 overflow-hidden rounded-xl bg-gray-100 shadow-sm dark:bg-white/10">
                {item.image ? (
                  <ImageWithFallback src={item.image} alt={item.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gray-100 dark:bg-white/10" />
                )}
                {resolvedOfferMeta?.badge && (
                  <div
                    className={`absolute left-1 top-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white shadow-md ${
                      resolvedOfferMeta.tone === 'red'
                        ? 'bg-rose-500'
                        : resolvedOfferMeta.tone === 'green'
                          ? 'bg-emerald-500'
                          : 'bg-amber-500'
                    }`}
                  >
                    {resolvedOfferMeta.badge}
                  </div>
                )}
              </div>
            </div>
          </div>

		          <div className="mt-3 flex items-center justify-between gap-2">
		            <div className="min-w-0 mr-auto flex flex-wrap items-center gap-2">
		              <span className="text-[13px] min-[360px]:text-[14px] font-extrabold text-gray-900 dark:text-white">
		                {effectiveFinalPrice !== null ? formatMoney(effectiveFinalPrice) : formatMoney(item.price)}
		              </span>
		              {effectiveFinalPrice !== null && pricingMeta?.originalPrice !== undefined && (
		                <span className="text-[11px] min-[360px]:text-[12px] font-semibold text-gray-500 line-through dark:text-white/50">
		                  {formatMoney(pricingMeta.originalPrice)}
		                </span>
		              )}
		            </div>
	
		            <div className="flex items-center gap-2">
		              {servingSizeLabel && (
		                <span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-700 shadow-sm dark:bg-white/15 dark:text-white/90">
		                  {servingSizeLabel}
		                </span>
		              )}
	
			              {!canOrderInApp ? (
			                <button
			                  type="button"
			                  disabled
			                  className="inline-flex min-h-[34px] items-center justify-center rounded-full bg-gray-100 px-4 py-2 text-[10px] font-bold leading-none text-gray-500 shadow-sm cursor-not-allowed dark:bg-white/10 dark:text-white/40"
			                >
			                  View Only
			                </button>
			              ) : totalInCart === 0 ? (
			                <button
		                  onClick={() => {
		                    if (isMenuRedirect) navigateToPublicMenu(item.id);
		                    if (hasCustomizations) {
		                      handleQuickAddToCart(item);
		                    } else {
		                      handleAddToCart(item, null, [], '');
		                    }
		                  }}
			                  className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[#2563EB] bg-white px-4 py-2 text-[9px] min-[360px]:text-[10px] font-extrabold leading-none text-[#2563EB] shadow-sm transition-colors hover:bg-blue-50 dark:bg-white/10 dark:border-white/20 dark:text-white dark:hover:bg-white/15"
			                >
			                  <Plus className="h-4 w-4" strokeWidth={3} />
			                  {t('common.add', 'ADD')}
			                </button>
			              ) : (
			                <div className="flex min-h-[34px] min-w-[92px] items-center justify-between gap-2 rounded-full bg-[#2563EB] px-2.5 py-2 text-white shadow-md">
			                  <button
			                    onClick={() => {
			                      const cartItem = Array.from(cart.values()).find(ci => ci.item.id === item.id);
		                      if (cartItem) decrementCartItem(cartItem.cartKey);
		                    }}
		                    className="rounded p-1 transition-colors hover:bg-white/20"
		                    aria-label="Decrease"
		                  >
		                    <Minus className="h-3.5 w-3.5" strokeWidth={3} />
		                  </button>
		                  <span className="min-w-[1.25rem] text-center text-[12px] font-extrabold">{totalInCart}</span>
		                  <button
		                    onClick={() => {
		                      const cartItem = Array.from(cart.values()).find(ci => ci.item.id === item.id);
		                      if (cartItem) incrementCartItem(cartItem.cartKey);
		                    }}
		                    className="rounded p-1 transition-colors hover:bg-white/20"
		                    aria-label="Increase"
		                  >
		                    <Plus className="h-3.5 w-3.5" strokeWidth={3} />
		                  </button>
		                </div>
		              )}
		            </div>
		          </div>
	
		          {hasCustomizations && totalInCart === 0 && canOrderInApp && (
		            <div className="mt-1 flex justify-end">
		              <span className="text-[10px] font-semibold text-gray-500 dark:text-white/55">
		                {t('menu.customisable', 'customisable')}
		              </span>
		            </div>
		          )}
        </div>

        {/* Desktop/Tablet: existing card */}
        <div className="hidden sm:flex sm:h-full sm:flex-col">
          <div className="relative h-[170px] w-full overflow-hidden">
            {item.image ? (
              <ImageWithFallback src={item.image} alt={item.name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-gray-100 dark:bg-white/10" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent" />

            <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
              {getDietIndicator()}
	              {item.tags.includes('bestseller') && (
	                <span className="rounded-full bg-gray-100/95 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide text-gray-800 shadow-sm dark:bg-white/15 dark:text-white/90 dark:backdrop-blur">
	                  {t('menu.badge.bestseller', 'Bestseller')}
	                </span>
	              )}
            </div>

	            {resolvedOfferMeta?.badge && (
	              <div
	                className={`absolute right-3 top-3 z-10 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-lg ${
	                  resolvedOfferMeta.tone === 'red'
	                    ? 'bg-rose-500'
	                    : resolvedOfferMeta.tone === 'green'
                      ? 'bg-emerald-500'
                      : 'bg-amber-500'
                }`}
              >
                {resolvedOfferMeta.badge}
              </div>
            )}
          </div>

	          <div className="flex flex-1 flex-col bg-white px-4 py-4 dark:bg-slate-900/35">
	            <div className="mb-2 flex items-start justify-between gap-3">
	              <h4 className="min-w-0 text-[16px] font-semibold leading-snug text-gray-900 line-clamp-1 dark:text-white">{item.name}</h4>
	              {(servingSizeLabel || showRating) && (
	                <div className="flex flex-shrink-0 items-center gap-2">
	                  {servingSizeLabel && (
	                    <span className="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-[9px] font-semibold text-gray-700 dark:bg-white/15 dark:text-white/90 dark:backdrop-blur">
	                      {servingSizeLabel}
	                    </span>
	                  )}
	                  {showRating && (
	                    <div className="flex items-center gap-1 whitespace-nowrap text-[13px] font-extrabold text-amber-600 dark:text-amber-300">
	                      <Star className="h-4 w-4 text-amber-500 fill-amber-500 dark:text-amber-400 dark:fill-amber-400" />
	                      <span>{ratingValue.toFixed(1)}</span>
	                    </div>
	                  )}
	                </div>
	              )}
	            </div>

	            <div className="mb-3 min-h-[32px] text-[11px] leading-relaxed text-gray-600 dark:text-white/70" aria-hidden={!hasDescription}>
	              {!hasDescription ? (
	                <p className="opacity-0">—</p>
              ) : isDescriptionExpanded ? (
                <p>
                  <span className="whitespace-pre-line">{descriptionText}</span>
	                  <button
	                    type="button"
	                    onClick={() => setIsDescriptionExpanded(false)}
	                    className="ml-1 inline text-[10px] font-extrabold text-[#2563EB] hover:text-[#1D4ED8] dark:text-amber-300 dark:hover:text-amber-200"
	                  >
	                    {t('common.less', 'less')}
	                  </button>
                </p>
              ) : (
                <p className="relative block">
                  <span ref={desktopDescRef} className="line-clamp-2">
                    {descriptionText}
                  </span>
	                  {shouldShowDesktopMore && (
	                    <button
	                      type="button"
	                      onClick={() => setIsDescriptionExpanded(true)}
	                      className="absolute bottom-[2px] right-0 rounded-sm bg-white/95 px-1 text-[10px] font-extrabold leading-none text-[#2563EB] shadow-sm hover:text-[#1D4ED8] dark:bg-slate-900/70 dark:text-amber-300 dark:hover:text-amber-200 dark:shadow-none"
	                    >
	                      …{t('common.more', 'more')}
	                    </button>
	                  )}
                </p>
              )}
            </div>

	            <div className="mt-auto flex items-end justify-between gap-3">
	              <div className="min-w-0">
	                <div className="flex flex-wrap items-center gap-2">
	                  <span className="text-[17px] font-bold text-gray-900 dark:text-white">
	                    {effectiveFinalPrice !== null ? formatMoney(effectiveFinalPrice) : formatMoney(item.price)}
	                  </span>
	                  {effectiveFinalPrice !== null && pricingMeta?.originalPrice !== undefined && (
	                    <span className="text-[13px] font-medium text-gray-500 line-through dark:text-white/50">
	                      {formatMoney(pricingMeta.originalPrice)}
	                    </span>
	                  )}
	                </div>
	              </div>

              <div className="flex flex-col items-end gap-1">
	                {!canOrderInApp ? (
	                  <button
	                    type="button"
	                    disabled
	                    className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-6 py-2.5 text-[12px] font-bold text-gray-500 shadow-sm cursor-not-allowed dark:bg-white/10 dark:text-white/40"
	                  >
	                    View Only
	                  </button>
	                ) : totalInCart === 0 ? (
	                  <button
                    onClick={() => {
                      if (isMenuRedirect) navigateToPublicMenu(item.id);
                      if (hasCustomizations) {
                        handleQuickAddToCart(item);
                      } else {
                        handleAddToCart(item, null, [], '');
                      }
                    }}
	                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2563EB] px-5 py-2.5 text-[12px] font-extrabold text-white shadow-md transition-colors hover:bg-[#1D4ED8]"
	                  >
	                    <Plus className="w-4 h-4" strokeWidth={3} />
	                    {t('common.add', 'ADD')}
	                  </button>
                ) : (
	                  <div className="flex items-center justify-between gap-3 rounded-xl bg-[#2563EB] px-3 py-2 text-white shadow-md">
                    <button
                      onClick={() => {
                        const cartItem = Array.from(cart.values()).find(ci => ci.item.id === item.id);
                        if (cartItem) decrementCartItem(cartItem.cartKey);
                      }}
                      className="rounded p-1 transition-colors hover:bg-white/20"
                    >
                      <Minus className="w-4 h-4" strokeWidth={3} />
                    </button>
	                    <span className="min-w-[1.5rem] text-center font-extrabold text-[14px]">{totalInCart}</span>
                    <button
                      onClick={() => {
                        const cartItem = Array.from(cart.values()).find(ci => ci.item.id === item.id);
                        if (cartItem) incrementCartItem(cartItem.cartKey);
                      }}
                      className="rounded p-1 transition-colors hover:bg-white/20"
                    >
                      <Plus className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </div>
                )}
                {hasCustomizations && totalInCart === 0 && canOrderInApp && (
                  <span className="text-xs text-gray-500 dark:text-white/55">{t('menu.customisable', 'customisable')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}



